<template>
  <div class="p-3 pb-16 bg mb-auto h-full overflow-auto bg-gradient-to-b from-gray-800 text-white" id="style-1">
    <!-- Loading and Error States -->
    <div v-if="loading" class="flex items-center justify-center h-screen">
      <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
    </div>
    <div v-else-if="error" class="text-center text-red-300">
      <p>{{ error }}</p>
    </div>
    <div v-else-if="characterData && characterPrivateData" class="space-y-6">
      <!-- Character Status with Progress Bars -->
      <div class="bg-gray-900 bg-opacity-80 border border-gray-800 p-5 rounded-lg shadow-xl">
        <h3 class="text-xl font-semibold text-indigo-300 mb-3">สถานะตัวละคร</h3>
        <div class="grid grid-cols-1 sm:grid-cols-5 gap-3">
          <!-- HP -->
          <div class="flex flex-col p-2 rounded-lg shadow-md" :class="{ 'bg-red-900': hpPercentage <= 20, 'bg-gray-800': hpPercentage > 20 }">
            <div class="flex justify-between items-center space-x-3">
              <div class="flex items-center space-x-2">
                <i class="fa-solid fa-heart text-red-500 text-sm"></i>
                <span class="font-bold text-sm" :class="{ 'text-red-500': hpPercentage <= 20, 'text-white': hpPercentage > 20 }">{{ playerHp }}/{{ playerMaxHp }}</span>
              </div>
              <div class="text-xs text-gray-400">พลังชีวิต</div>
            </div>
            <div class="w-full bg-gray-700 rounded-full h-2 mt-1">
              <div class="bg-red-500 h-2 rounded-full" :style="{ width: `${hpPercentage}%` }"></div>
            </div>
          </div>
          <!-- Energy -->
          <div class="flex flex-col p-2 rounded-lg shadow-md" :class="{ 'bg-red-900': energyPercentage <= 10, 'bg-gray-800': energyPercentage > 10 }">
            <div class="flex justify-between items-center space-x-3">
              <div class="flex items-center space-x-2">
                <i class="fa-solid fa-bolt text-yellow-400 text-sm"></i>
                <span class="font-bold text-sm" :class="{ 'text-red-500': energyPercentage <= 10, 'text-white': energyPercentage > 10 }">{{ characterPrivateData.energyLevel || 0 }}/4800</span>
              </div>
              <div class="text-xs text-gray-400">พลังงาน</div>
            </div>
            <div class="w-full bg-gray-700 rounded-full h-2 mt-1">
              <div class="bg-yellow-500 h-2 rounded-full" :style="{ width: `${energyPercentage}%` }"></div>
            </div>
          </div>
          <!-- Food -->
          <div class="flex flex-col p-2 rounded-lg shadow-md" :class="{ 'bg-red-900': (characterPrivateData.foodLevel || 0) <= 10, 'bg-gray-800': (characterPrivateData.foodLevel || 0) > 10 }">
            <div class="flex justify-between items-center space-x-3">
              <div class="flex items-center space-x-2">
                <i class="fa-solid fa-drumstick-bite text-green-400 text-sm"></i>
                <span class="font-bold text-sm" :class="{ 'text-red-500': (characterPrivateData.foodLevel || 0) <= 10, 'text-white': (characterPrivateData.foodLevel || 0) > 10 }">{{ characterPrivateData.foodLevel || 0 }}/100</span>
              </div>
              <div class="text-xs text-gray-400">อาหาร</div>
            </div>
            <div class="w-full bg-gray-700 rounded-full h-2 mt-1">
              <div class="bg-green-500 h-2 rounded-full" :style="{ width: `${foodPercentage}%` }"></div>
            </div>
          </div>
          <!-- Happiness -->
          <div class="flex flex-col p-2 rounded-lg shadow-md" :class="{ 'bg-red-900': (characterPrivateData.happyLevel || 0) <= 10, 'bg-gray-800': (characterPrivateData.happyLevel || 0) > 10 }">
            <div class="flex justify-between items-center space-x-3">
              <div class="flex items-center space-x-2">
                <i class="fa-solid fa-smile text-blue-400 text-sm"></i>
                <span class="font-bold text-sm" :class="{ 'text-red-500': (characterPrivateData.happyLevel || 0) <= 10, 'text-white': (characterPrivateData.happyLevel || 0) > 10 }">{{ characterPrivateData.happyLevel || 0 }}/100</span>
              </div>
              <div class="text-xs text-gray-400">ความสุข</div>
            </div>
            <div class="w-full bg-gray-700 rounded-full h-2 mt-1">
              <div class="bg-blue-500 h-2 rounded-full" :style="{ width: `${happinessPercentage}%` }"></div>
            </div>
          </div>
          <!-- Infection -->
          <div class="flex flex-col p-2 rounded-lg shadow-md" :class="{ 'bg-red-900': (characterPrivateData.infectionLevel || 0) >= 90, 'bg-gray-800': (characterPrivateData.infectionLevel || 0) < 90 }">
            <div class="flex justify-between items-center space-x-3">
              <div class="flex items-center space-x-2">
                <i class="fa-solid fa-biohazard text-purple-400 text-sm"></i>
                <span class="font-bold text-sm" :class="{ 'text-red-500': (characterPrivateData.infectionLevel || 0) >= 90, 'text-white': (characterPrivateData.infectionLevel || 0) < 90 }">{{ characterPrivateData.infectionLevel || 0 }}/100</span>
              </div>
              <div class="text-xs text-gray-400">เชื้อโรค</div>
            </div>
            <div class="w-full bg-gray-700 rounded-full h-2 mt-1">
              <div class="bg-purple-500 h-2 rounded-full" :style="{ width: `${infectionPercentage}%` }"></div>
            </div>
          </div>
        </div>
      </div>
      <!-- Level and Stat Points Section -->
      <div class="mb-6 bg-gray-900 bg-opacity-80 border border-gray-800 p-5 rounded-lg shadow-xl">
        <h4 class="text-sm font-semibold text-indigo-300 mb-3">ตรวจสอบและอัปเดตเลเวลตัวละคร</h4>
        <div class="text-xs">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">เลเวลปัจจุบัน</h5>
              <p class="text-indigo-200">{{ characterData.level }}</p>
            </div>
            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">แต้มสถานะปัจจุบัน</h5>
              <p class="text-indigo-200">{{ characterPrivateData.statPoints }}</p>
            </div>
          </div>
          <div class="mt-3 flex items-center gap-3">
            <input
              v-model="newLevel"
              type="number"
              min="1"
              placeholder="กรอกเลเวลใหม่"
              class="p-2 rounded-lg bg-gray-700 border border-gray-600 text-indigo-200 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-all duration-200"
            />
            <button
              @click="updateLevel"
              :disabled="isUpdating || !newLevel || newLevel <= 0"
              class="bg-indigo-600 hover:bg-indigo-500 text-sm px-4 py-2 rounded-lg transition-colors border border-indigo-700 disabled:opacity-50"
              :class="{ 'cursor-not-allowed opacity-70': isUpdating || !newLevel || newLevel <= 0 }"
            >
              <div class="btn-content">
                <i class="fas fa-level-up-alt text-base"></i>
                <span class="text-xs font-semibold">อัปเดตเลเวล</span>
              </div>
            </button>
          </div>
          <p class="mt-2 text-xs text-yellow-300">
            การอัปเดตเลเวลจะรีเซ็ตสถานะพื้นฐานและให้แต้มสถานะใหม่ตามเลเวล (สูตร: (เลเวล - 1) * 5) และอัปเดตเลเวลใน characters ด้วย
          </p>
        </div>
      </div>
      <!-- Resources Section -->
      <div class="bg-gray-900 bg-opacity-80 border border-gray-800 p-5 rounded-lg shadow-xl">
        <h3 class="text-xl font-semibold text-indigo-300 mb-3">ทรัพยากร</h3>
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <!-- Cash -->
          <div class="flex items-center justify-between bg-gradient-to-r from-green-900 to-gray-800 p-2 rounded-lg shadow-md transition-all duration-300 hover:shadow-green-500/50 hover:scale-105">
            <div class="flex items-center space-x-2">
              <i class="fa-solid fa-money-bill text-green-400 text-sm"></i>
              <span class="font-bold text-sm text-white">{{ userData?.cash || 0 }}</span>
            </div>
            <span class="text-xs text-gray-400 hidden sm:block">แคช</span>
          </div>
          <!-- Diamond -->
          <div class="flex items-center justify-between bg-gradient-to-r from-blue-900 to-gray-800 p-2 rounded-lg shadow-md transition-all duration-300 hover:shadow-blue-500/50 hover:scale-105">
            <div class="flex items-center space-x-2">
              <i class="fa-solid fa-gem text-blue-400 text-sm"></i>
              <span class="font-bold text-sm text-white">{{ characterData?.diamond || 0 }}</span>
            </div>
            <span class="text-xs text-gray-400 hidden sm:block">เพชร</span>
          </div>
          <!-- Gold -->
          <div class="flex items-center justify-between bg-gradient-to-r from-yellow-900 to-gray-800 p-2 rounded-lg shadow-md transition-all duration-300 hover:shadow-yellow-500/50 hover:scale-105">
            <div class="flex items-center space-x-2">
              <i class="fa-solid fa-g text-yellow-300 text-sm"></i>
              <span class="font-bold text-sm text-white">{{ characterData?.gold || 0 }}</span>
            </div>
            <span class="text-xs text-gray-400 hidden sm:block">ทอง</span>
          </div>
          <!-- Silver -->
          <div class="flex items-center justify-between bg-gradient-to-r from-gray-700 to-gray-800 p-2 rounded-lg shadow-md transition-all duration-300 hover:shadow-gray-400/50 hover:scale-105">
            <div class="flex items-center space-x-2">
              <i class="fa-solid fa-coins text-gray-300 text-sm"></i>
              <span class="font-bold text-sm text-white">{{ characterData?.silver || 0 }}</span>
            </div>
            <span class="text-xs text-gray-400 hidden sm:block">เหรียญ</span>
          </div>
        </div>
      </div>
      <!-- Currency Update Section -->
      <div class="mb-6 bg-gray-900 bg-opacity-80 border border-gray-800 p-5 rounded-lg shadow-xl">
        <h4 class="text-sm font-semibold text-indigo-300 mb-3">อัปเดตสกุลเงิน</h4>
        <div class="text-xs">
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">Silver ปัจจุบัน</h5>
              <p class="text-indigo-200">{{ characterData.silver }}</p>
            </div>
            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">Gold ปัจจุบัน</h5>
              <p class="text-indigo-200">{{ characterData.gold }}</p>
            </div>
            <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
              <h5 class="font-semibold text-indigo-300 text-sm">Diamond ปัจจุบัน</h5>
              <p class="text-indigo-200">{{ characterData.diamond }}</p>
            </div>
          </div>
          <div class="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div class="flex items-center gap-3">
              <input
                v-model="addSilver"
                type="number"
                min="0"
                placeholder="เพิ่ม Silver"
                class="p-2 rounded-lg bg-gray-700 border border-gray-600 text-indigo-200 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-all duration-200"
              />
              <button
                @click="updateSilver"
                :disabled="isUpdating || !addSilver || addSilver < 0"
                class="bg-indigo-600 hover:bg-indigo-500 text-sm px-4 py-2 rounded-lg transition-colors border border-indigo-700 disabled:opacity-50"
                :class="{ 'cursor-not-allowed opacity-70': isUpdating || !addSilver || addSilver < 0 }"
              >
                <div class="btn-content">
                  <i class="fas fa-plus text-base"></i>
                  <span class="text-xs font-semibold">เพิ่ม Silver</span>
                </div>
              </button>
            </div>
            <div class="flex items-center gap-3">
              <input
                v-model="addGold"
                type="number"
                min="0"
                placeholder="เพิ่ม Gold"
                class="p-2 rounded-lg bg-gray-700 border border-gray-600 text-indigo-200 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-all duration-200"
              />
              <button
                @click="updateGold"
                :disabled="isUpdating || !addGold || addGold < 0"
                class="bg-indigo-600 hover:bg-indigo-500 text-sm px-4 py-2 rounded-lg transition-colors border border-indigo-700 disabled:opacity-50"
                :class="{ 'cursor-not-allowed opacity-70': isUpdating || !addGold || addGold < 0 }"
              >
                <div class="btn-content">
                  <i class="fas fa-plus text-base"></i>
                  <span class="text-xs font-semibold">เพิ่ม Gold</span>
                </div>
              </button>
            </div>
            <div class="flex items-center gap-3">
              <input
                v-model="addDiamond"
                type="number"
                min="0"
                placeholder="เพิ่ม Diamond"
                class="p-2 rounded-lg bg-gray-700 border border-gray-600 text-indigo-200 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-all duration-200"
              />
              <button
                @click="updateDiamond"
                :disabled="isUpdating || !addDiamond || addDiamond < 0"
                class="bg-indigo-600 hover:bg-indigo-500 text-sm px-4 py-2 rounded-lg transition-colors border border-indigo-700 disabled:opacity-50"
                :class="{ 'cursor-not-allowed opacity-70': isUpdating || !addDiamond || addDiamond < 0 }"
              >
                <div class="btn-content">
                  <i class="fas fa-plus text-base"></i>
                  <span class="text-xs font-semibold">เพิ่ม Diamond</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- Character Info -->
      <div class="bg-gray-900 bg-opacity-80 border border-gray-800 p-5 rounded-lg shadow-xl">
        <div class="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
          <div class="md:col-span-3 flex justify-center">
            <div class="relative">
              <img
                :src="characterData.imageUrl || 'https://via.placeholder.com/112'"
                class="w-28 h-28 object-cover rounded-md border-2 border-gray-600 transition-all duration-200 hover:border-indigo-400"
              />
              <div class="absolute -bottom-2 -right-2 bg-indigo-600 text-white text-sm font-semibold px-2 py-1 rounded-full">
                Lv. {{ characterData.level || 1 }}
              </div>
            </div>
          </div>
          <div class="md:col-span-6 text-center md:text-left">
            <div class="flex items-center justify-center md:justify-start space-x-4 mb-2">
              <h1 class="text-xl font-semibold text-indigo-300">{{ characterData.characterName || 'Unknown' }}</h1>
            </div>
            <div class="flex items-center space-x-2 w-full">
              <div class="flex-1">
                <div v-if="characterData.level < 30" class="mt-2 flex justify-center md:justify-start items-center space-x-3 text-sm text-indigo-200">
                  <i class="fas fa-star text-indigo-400 text-sm"></i>
                  <span>ค่าประสบการณ์ ({{ characterPrivateData.experience || 0 }} / {{ nextLevelExp }})</span>
                </div>
                <div v-if="characterData.level < 30" class="w-full bg-gray-600 rounded-full h-2 mt-1">
                  <div class="bg-indigo-600 h-2 rounded-full" :style="{ width: `${experiencePercentage}%` }"></div>
                </div>
                <div v-else class="mt-2 flex justify-center md:justify-start items-center space-x-3 text-sm text-indigo-200">
                  <i class="fas fa-star text-indigo-400 text-sm"></i>
                  <span>เลเวลเต็มแล้ว</span>
                </div>
              </div>
            </div>
          </div>
          <div class="md:col-span-3 flex justify-center md:justify-end">
            <div class="flex items-center bg-gray-700 border border-gray-600 px-3 py-1 rounded-md transition-all duration-200 hover:bg-gray-600 hover:border-indigo-400">
              <i class="fa-solid fa-trophy text-yellow-400 mr-2 text-sm"></i>
              <span class="text-sm font-semibold text-indigo-200">{{ characterData.renown || 0 }}</span>
            </div>
          </div>
        </div>
      </div>
      <!-- Stats and Power -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div class="bg-gray-900 bg-opacity-80 border border-gray-800 p-5 rounded-lg shadow-xl">
          <h3 class="text-xl font-semibold text-indigo-300 mb-3">ค่าสเตตัส</h3>
          <div class="grid grid-cols-1 sm:grid-cols-5 gap-3">
            <div
              class="text-center p-2 bg-gray-800 bg-opacity-80 border border-gray-700 rounded-md transition-all duration-200 hover:bg-gray-600 hover:border-indigo-400 cursor-pointer"
              @click="showStatDetails('stamina')"
            >
              <i class="fas fa-heart text-indigo-400 text-lg mb-1"></i>
              <div class="text-lg font-semibold text-white">{{ totalStats.stamina }}</div>
              <div class="text-xs text-indigo-200">STAMINA</div>
            </div>
            <div
              class="text-center p-2 bg-gray-800 bg-opacity-80 border border-gray-700 rounded-md transition-all duration-200 hover:bg-gray-600 hover:border-indigo-400 cursor-pointer"
              @click="showStatDetails('str')"
            >
              <i class="fas fa-dumbbell text-indigo-400 text-lg mb-1"></i>
              <div class="text-lg font-semibold text-white">{{ totalStats.str }}</div>
              <div class="text-xs text-indigo-200">STR</div>
            </div>
            <div
              class="text-center p-2 bg-gray-800 bg-opacity-80 border border-gray-700 rounded-md transition-all duration-200 hover:bg-gray-600 hover:border-indigo-400 cursor-pointer"
              @click="showStatDetails('int')"
            >
              <i class="fas fa-brain text-indigo-400 text-lg mb-1"></i>
              <div class="text-lg font-semibold text-white">{{ totalStats.int }}</div>
              <div class="text-xs text-indigo-200">INT</div>
            </div>
            <div
              class="text-center p-2 bg-gray-800 bg-opacity-80 border border-gray-700 rounded-md transition-all duration-200 hover:bg-gray-600 hover:border-indigo-400 cursor-pointer"
              @click="showStatDetails('agi')"
            >
              <i class="fas fa-running text-indigo-400 text-lg mb-1"></i>
              <div class="text-lg font-semibold text-white">{{ totalStats.agi }}</div>
              <div class="text-xs text-indigo-200">AGI</div>
            </div>
            <div
              class="text-center p-2 bg-gray-800 bg-opacity-80 border border-gray-700 rounded-md transition-all duration-200 hover:bg-gray-600 hover:border-indigo-400 cursor-pointer"
              @click="showStatDetails('luck')"
            >
              <i class="fas fa-clover text-indigo-400 text-lg mb-1"></i>
              <div class="text-lg font-semibold text-white">{{ totalStats.luck }}</div>
              <div class="text-xs text-indigo-200">LUCK</div>
            </div>
          </div>
        </div>
        <div class="bg-gray-900 bg-opacity-80 border border-gray-800 p-5 rounded-lg shadow-xl">
          <h3 class="text-xl font-semibold text-indigo-300 mb-3">ค่าพลัง</h3>
          <div class="space-y-2">
            <div
              class="flex justify-between items-center py-1 border-b border-gray-800 cursor-pointer transition-all duration-200 hover:bg-gray-700"
              @click="showPowerDetails('maxHp')"
            >
              <span class="text-sm text-indigo-200 flex items-center">
                <i class="fas fa-heartbeat text-indigo-400 mr-2 text-sm"></i> พลังชีวิตสูงสุด
              </span>
              <span class="text-sm font-semibold text-white">{{ calculatedStats.maxHp }}</span>
            </div>
            <div
              class="flex justify-between items-center py-1 border-b border-gray-800 cursor-pointer transition-all duration-200 hover:bg-gray-700"
              @click="showPowerDetails('atk')"
            >
              <span class="text-sm text-indigo-200 flex items-center">
                <i class="fas fa-fist-raised text-indigo-400 mr-2 text-sm"></i> พลังโจมตี
              </span>
              <span class="text-sm font-semibold text-white">{{ calculatedStats.atk }}</span>
            </div>
            <div
              class="flex justify-between items-center py-1 border-b border-gray-800 cursor-pointer transition-all duration-200 hover:bg-gray-700"
              @click="showPowerDetails('critical')"
            >
              <span class="text-sm text-indigo-200 flex items-center">
                <i class="fas fa-crosshairs text-indigo-400 mr-2 text-sm"></i> คริติคอล
              </span>
              <span class="text-sm font-semibold text-white">{{ calculatedStats.critical }}%</span>
            </div>
            <div
              class="flex justify-between items-center py-1 border-b border-gray-800 cursor-pointer transition-all duration-200 hover:bg-gray-700"
              @click="showPowerDetails('evasion')"
            >
              <span class="text-sm text-indigo-200 flex items-center">
                <i class="fas fa-wind text-indigo-400 mr-2 text-sm"></i> หลบหลีก
              </span>
              <span class="text-sm font-semibold text-white">{{ calculatedStats.evasion }}%</span>
            </div>
            <div
              class="flex justify-between items-center py-1 border-b border-gray-800 cursor-pointer transition-all duration-200 hover:bg-gray-700"
              @click="showPowerDetails('criticalReduction')"
            >
              <span class="text-sm text-indigo-200 flex items-center">
                <i class="fas fa-shield-virus text-indigo-400 mr-2 text-sm"></i> ลดคริติคอล
              </span>
              <span class="text-sm font-semibold text-white">{{ calculatedStats.criticalReduction }}%</span>
            </div>
            <div
              class="flex justify-between items-center py-1 border-b border-gray-800 cursor-pointer transition-all duration-200 hover:bg-gray-700"
              @click="showPowerDetails('dropRateBonus')"
            >
              <span class="text-sm text-indigo-200 flex items-center">
                <i class="fas fa-gift text-indigo-400 mr-2 text-sm"></i> โบนัสดรอป
              </span>
              <span class="text-sm font-semibold text-white">{{ calculatedStats.dropRateBonus }}%</span>
            </div>
            <div
              class="flex justify-between items-center py-1 border-b border-gray-800 cursor-pointer transition-all duration-200 hover:bg-gray-700"
              @click="showPowerDetails('damageReduction')"
            >
              <span class="text-sm text-indigo-200 flex items-center">
                <i class="fa-solid fa-person-rays text-indigo-400 mr-2 text-sm"></i> ลดดาเมจ
              </span>
              <span class="text-sm font-semibold text-white">{{ calculatedStats.damageReduction }}%</span>
            </div>
            <div
              class="flex justify-between items-center py-1 cursor-pointer transition-all duration-200 hover:bg-gray-700"
              @click="showPowerDetails('defense')"
            >
              <span class="text-sm text-indigo-200 flex items-center">
                <i class="fas fa-shield-alt text-indigo-400 mr-2 text-sm"></i> พลังป้องกัน
              </span>
              <span class="text-sm font-semibold text-white">{{ calculatedStats.defense }}</span>
            </div>
          </div>
        </div>
      </div>
      <!-- Verification Sections -->
      <div class="bg-gray-900 bg-opacity-80 border border-gray-800 p-5 rounded-lg shadow-xl">
        <h3 class="text-xl font-semibold text-indigo-300 mb-3">การตรวจสอบข้อมูล</h3>
        <!-- Item Stats Comparison -->
        <div class="mb-6">
          <h4 class="text-sm font-semibold text-indigo-300 mb-3">เปรียบเทียบ Item Stats กับ Items</h4>
          <div class="text-xs">
            <p class="text-indigo-200">จำนวนไอเทมปกติ: {{ equippedItems.length }}</p>
            <p class="text-indigo-200">จำนวนอวัยวะจักรกล: {{ equippedMechanicalOrgans.length }}</p>
            <div class="grid grid-cols-1 sm:grid-cols-4 gap-3 mt-2">
              <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
                <h5 class="font-semibold text-indigo-300 text-sm">Item Stats (จาก characterPrivate)</h5>
                <div class="text-indigo-200 space-y-1">
                  <p>Stamina: {{ characterPrivateData.itemStats?.stamina || 0 }}</p>
                  <p>Strength: {{ characterPrivateData.itemStats?.str || 0 }}</p>
                  <p>Intelligence: {{ characterPrivateData.itemStats?.int || 0 }}</p>
                  <p>Agility: {{ characterPrivateData.itemStats?.agi || 0 }}</p>
                  <p>Luck: {{ characterPrivateData.itemStats?.luck || 0 }}</p>
                  <p>Max HP: {{ characterPrivateData.itemStats?.maxHp || 0 }}</p>
                  <p>Attack: {{ characterPrivateData.itemStats?.atk || 0 }}</p>
                  <p>Evasion: {{ characterPrivateData.itemStats?.evasion || 0 }}</p>
                  <p>Critical: {{ characterPrivateData.itemStats?.critical || 0 }}</p>
                  <p>Defense: {{ characterPrivateData.itemStats?.defense || 0 }}</p>
                </div>
              </div>
              <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
                <h5 class="font-semibold text-indigo-300 text-sm">Calculated Total Item Stats (จาก Items ทั้งหมด)</h5>
                <div class="text-indigo-200 space-y-1">
                  <p>Stamina: {{ calculatedItemStats.stamina }}</p>
                  <p>Strength: {{ calculatedItemStats.str }}</p>
                  <p>Intelligence: {{ calculatedItemStats.int }}</p>
                  <p>Agility: {{ calculatedItemStats.agi }}</p>
                  <p>Luck: {{ calculatedItemStats.luck }}</p>
                  <p>Max HP: {{ calculatedItemStats.maxHp }}</p>
                  <p>Attack: {{ calculatedItemStats.atk }}</p>
                  <p>Evasion: {{ calculatedItemStats.evasion }}</p>
                  <p>Critical: {{ calculatedItemStats.critical }}</p>
                  <p>Defense: {{ calculatedItemStats.defense }}</p>
                </div>
              </div>
              <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
                <h5 class="font-semibold text-indigo-300 text-sm">Calculated Normal Item Stats (จาก Items ปกติ)</h5>
                <div class="text-indigo-200 space-y-1">
                  <p>Stamina: {{ calculatedNormalItemStats.stamina }}</p>
                  <p>Strength: {{ calculatedNormalItemStats.str }}</p>
                  <p>Intelligence: {{ calculatedNormalItemStats.int }}</p>
                  <p>Agility: {{ calculatedNormalItemStats.agi }}</p>
                  <p>Luck: {{ calculatedNormalItemStats.luck }}</p>
                  <p>Max HP: {{ calculatedNormalItemStats.maxHp }}</p>
                  <p>Attack: {{ calculatedNormalItemStats.atk }}</p>
                  <p>Evasion: {{ calculatedNormalItemStats.evasion }}</p>
                  <p>Critical: {{ calculatedNormalItemStats.critical }}</p>
                  <p>Defense: {{ calculatedNormalItemStats.defense }}</p>
                </div>
              </div>
              <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
                <h5 class="font-semibold text-indigo-300 text-sm">Calculated Mechanical Item Stats (จาก อวัยวะจักรกล)</h5>
                <div class="text-indigo-200 space-y-1">
                  <p>Stamina: {{ calculatedMechanicalItemStats.stamina }}</p>
                  <p>Strength: {{ calculatedMechanicalItemStats.str }}</p>
                  <p>Intelligence: {{ calculatedMechanicalItemStats.int }}</p>
                  <p>Agility: {{ calculatedMechanicalItemStats.agi }}</p>
                  <p>Luck: {{ calculatedMechanicalItemStats.luck }}</p>
                  <p>Max HP: {{ calculatedMechanicalItemStats.maxHp }}</p>
                  <p>Attack: {{ calculatedMechanicalItemStats.atk }}</p>
                  <p>Evasion: {{ calculatedMechanicalItemStats.evasion }}</p>
                  <p>Critical: {{ calculatedMechanicalItemStats.critical }}</p>
                  <p>Defense: {{ calculatedMechanicalItemStats.defense }}</p>
                </div>
              </div>
            </div>
            <p
              class="mt-3 text-sm"
              :class="isItemStatsMatching ? 'text-green-300' : 'text-red-300'"
            >
              {{ isItemStatsMatching ? 'สถานะไอเทมตรงกัน' : 'สถานะไอเทมไม่ตรงกัน' }}
            </p>
            <button
              v-if="!isItemStatsMatching"
              @click="updateItemStats"
              :disabled="isUpdating"
              class="bg-indigo-600 hover:bg-indigo-500 text-sm px-4 py-2 rounded-lg transition-colors border border-indigo-700 disabled:opacity-50 mt-2"
            >
              <i v-if="isUpdating" class="fas fa-spinner animate-spin mr-1"></i>
              อัปเดต Item Stats
            </button>
          </div>
        </div>
        <!-- Ghost Stats Comparison -->
        <div class="mb-6">
          <h4 class="text-sm font-semibold text-indigo-300 mb-3">เปรียบเทียบ Ghost Stats กับ Ghost Items</h4>
          <div class="text-xs">
            <p class="text-indigo-200">จำนวนวิญญาณ: {{ ghostItems.length }}</p>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
                <h5 class="font-semibold text-indigo-300 text-sm">Ghost Stats (จาก characterPrivate)</h5>
                <div class="text-indigo-200 space-y-1">
                  <p>Stamina: {{ characterPrivateData.ghostStats?.stamina || 0 }}</p>
                  <p>Strength: {{ characterPrivateData.ghostStats?.str || 0 }}</p>
                  <p>Intelligence: {{ characterPrivateData.ghostStats?.int || 0 }}</p>
                  <p>Agility: {{ characterPrivateData.ghostStats?.agi || 0 }}</p>
                  <p>Luck: {{ characterPrivateData.ghostStats?.luck || 0 }}</p>
                  <p>Max HP: {{ characterPrivateData.ghostStats?.maxHp || 0 }}</p>
                  <p>Attack: {{ characterPrivateData.ghostStats?.atk || 0 }}</p>
                  <p>Evasion: {{ characterPrivateData.ghostStats?.evasion || 0 }}</p>
                  <p>Critical: {{ characterPrivateData.ghostStats?.critical || 0 }}</p>
                  <p>Defense: {{ characterPrivateData.ghostStats?.defense || 0 }}</p>
                </div>
              </div>
              <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
                <h5 class="font-semibold text-indigo-300 text-sm">Calculated Ghost Stats (จาก Ghost Items)</h5>
                <div class="text-indigo-200 space-y-1">
                  <p>Stamina: {{ calculatedGhostStats.stamina }}</p>
                  <p>Strength: {{ calculatedGhostStats.str }}</p>
                  <p>Intelligence: {{ calculatedGhostStats.int }}</p>
                  <p>Agility: {{ calculatedGhostStats.agi }}</p>
                  <p>Luck: {{ calculatedGhostStats.luck }}</p>
                  <p>Max HP: {{ calculatedGhostStats.maxHp }}</p>
                  <p>Attack: {{ calculatedGhostStats.atk }}</p>
                  <p>Evasion: {{ calculatedGhostStats.evasion }}</p>
                  <p>Critical: {{ calculatedGhostStats.critical }}</p>
                  <p>Defense: {{ calculatedGhostStats.defense }}</p>
                </div>
              </div>
            </div>
            <p
              class="mt-3 text-sm"
              :class="isGhostStatsMatching ? 'text-green-300' : 'text-red-300'"
            >
              {{ isGhostStatsMatching ? 'สถานะวิญญาณตรงกัน' : 'สถานะวิญญาณไม่ตรงกัน' }}
            </p>
            <button
              v-if="!isGhostStatsMatching"
              @click="updateGhostStats"
              :disabled="isUpdating"
              class="bg-indigo-600 hover:bg-indigo-500 text-sm px-4 py-2 rounded-lg transition-colors border border-indigo-700 disabled:opacity-50 mt-2"
            >
              <i v-if="isUpdating" class="fas fa-spinner animate-spin mr-1"></i>
              อัปเดต Ghost Stats
            </button>
          </div>
        </div>
        <!-- Tattoo Stats Comparison -->
        <div class="mb-6">
          <h4 class="text-sm font-semibold text-indigo-300 mb-3">เปรียบเทียบ Tattoo Stats กับ Tattoos</h4>
          <div class="text-xs">
            <p class="text-indigo-200">จำนวนรอยสัก: {{ tattoos.length }}</p>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
                <h5 class="font-semibold text-indigo-300 text-sm">Tattoo Stats (จาก characterPrivate)</h5>
                <div class="text-indigo-200 space-y-1">
                  <p>Stamina: {{ characterPrivateData.tattooStats?.stamina || 0 }}</p>
                  <p>Strength: {{ characterPrivateData.tattooStats?.str || 0 }}</p>
                  <p>Intelligence: {{ characterPrivateData.tattooStats?.int || 0 }}</p>
                  <p>Agility: {{ characterPrivateData.tattooStats?.agi || 0 }}</p>
                  <p>Luck: {{ characterPrivateData.tattooStats?.luck || 0 }}</p>
                </div>
              </div>
              <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
                <h5 class="font-semibold text-indigo-300 text-sm">Calculated Tattoo Stats (จาก Tattoos)</h5>
                <div class="text-indigo-200 space-y-1">
                  <p>Stamina: {{ calculatedTattooStats.stamina }}</p>
                  <p>Strength: {{ calculatedTattooStats.str }}</p>
                  <p>Intelligence: {{ calculatedTattooStats.int }}</p>
                  <p>Agility: {{ calculatedTattooStats.agi }}</p>
                  <p>Luck: {{ calculatedTattooStats.luck }}</p>
                </div>
              </div>
            </div>
            <p
              class="mt-3 text-sm"
              :class="isTattooStatsMatching ? 'text-green-300' : 'text-red-300'"
            >
              {{ isTattooStatsMatching ? 'สถานะรอยสักตรงกัน' : 'สถานะรอยสักไม่ตรงกัน' }}
            </p>
            <button
              v-if="!isTattooStatsMatching"
              @click="updateTattooStats"
              :disabled="isUpdating"
              class="bg-indigo-600 hover:bg-indigo-500 text-sm px-4 py-2 rounded-lg transition-colors border border-indigo-700 disabled:opacity-50 mt-2"
            >
              <i v-if="isUpdating" class="fas fa-spinner animate-spin mr-1"></i>
              อัปเดต Tattoo Stats
            </button>
          </div>
        </div>
        <!-- Skill Stats Comparison -->
        <div class="mb-6">
          <h4 class="text-sm font-semibold text-indigo-300 mb-3">เปรียบเทียบ Skill Stats กับ Learned Skills</h4>
          <div class="text-xs">
            <p class="text-indigo-200">จำนวนสกิล: {{ skills.length }}</p>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
                <h5 class="font-semibold text-indigo-300 text-sm">Skill Stats (จาก characterPrivate)</h5>
                <div class="text-indigo-200 space-y-1">
                  <p>Stamina: {{ characterPrivateData.skillStats?.stamina || 0 }}</p>
                  <p>Strength: {{ characterPrivateData.skillStats?.str || 0 }}</p>
                  <p>Intelligence: {{ characterPrivateData.skillStats?.int || 0 }}</p>
                  <p>Agility: {{ characterPrivateData.skillStats?.agi || 0 }}</p>
                  <p>Luck: {{ characterPrivateData.skillStats?.luck || 0 }}</p>
                </div>
              </div>
              <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
                <h5 class="font-semibold text-indigo-300 text-sm">Calculated Skill Stats (จาก Learned Skills)</h5>
                <div class="text-indigo-200 space-y-1">
                  <p>Stamina: {{ calculatedSkillStats.stamina }}</p>
                  <p>Strength: {{ calculatedSkillStats.str }}</p>
                  <p>Intelligence: {{ calculatedSkillStats.int }}</p>
                  <p>Agility: {{ calculatedSkillStats.agi }}</p>
                  <p>Luck: {{ calculatedSkillStats.luck }}</p>
                </div>
              </div>
            </div>
            <p
              class="mt-3 text-sm"
              :class="isSkillStatsMatching ? 'text-green-300' : 'text-red-300'"
            >
              {{ isSkillStatsMatching ? 'สถานะสกิลตรงกัน' : 'สถานะสกิลไม่ตรงกัน' }}
            </p>
            <button
              v-if="!isSkillStatsMatching"
              @click="updateSkillStats"
              :disabled="isUpdating"
              class="bg-indigo-600 hover:bg-indigo-500 text-sm px-4 py-2 rounded-lg transition-colors border border-indigo-700 disabled:opacity-50 mt-2"
            >
              <i v-if="isUpdating" class="fas fa-spinner animate-spin mr-1"></i>
              อัปเดต Skill Stats
            </button>
          </div>
        </div>
        <!-- Title Stats Comparison -->
        <div class="mb-6">
          <h4 class="text-sm font-semibold text-indigo-300 mb-3">เปรียบเทียบ Title Stats กับ Current Title</h4>
          <div class="text-xs">
            <p class="text-indigo-200">ฉายาปัจจุบัน: {{ currentTitle || 'ไม่มีฉายา' }}</p>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
                <h5 class="font-semibold text-indigo-300 text-sm">Title Stats (จาก characterPrivate)</h5>
                <div class="text-indigo-200 space-y-1">
                  <p>Stamina: {{ characterPrivateData.titleStats?.stamina || 0 }}</p>
                  <p>Strength: {{ characterPrivateData.titleStats?.str || 0 }}</p>
                  <p>Intelligence: {{ characterPrivateData.titleStats?.int || 0 }}</p>
                  <p>Agility: {{ characterPrivateData.titleStats?.agi || 0 }}</p>
                  <p>Luck: {{ characterPrivateData.titleStats?.luck || 0 }}</p>
                </div>
              </div>
              <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
                <h5 class="font-semibold text-indigo-300 text-sm">Calculated Title Stats (จาก Current Title)</h5>
                <div class="text-indigo-200 space-y-1">
                  <p>Stamina: {{ calculatedTitleStats.stamina }}</p>
                  <p>Strength: {{ calculatedTitleStats.str }}</p>
                  <p>Intelligence: {{ calculatedTitleStats.int }}</p>
                  <p>Agility: {{ calculatedTitleStats.agi }}</p>
                  <p>Luck: {{ calculatedTitleStats.luck }}</p>
                </div>
              </div>
            </div>
            <p
              class="mt-3 text-sm"
              :class="isTitleStatsMatching ? 'text-green-300' : 'text-red-300'"
            >
              {{ isTitleStatsMatching ? 'สถานะฉายาตรงกัน' : 'สถานะฉายาไม่ตรงกัน' }}
            </p>
            <button
              v-if="!isTitleStatsMatching"
              @click="updateTitleStats"
              :disabled="isUpdating"
              class="bg-indigo-600 hover:bg-indigo-500 text-sm px-4 py-2 rounded-lg transition-colors border border-indigo-700 disabled:opacity-50 mt-2"
            >
              <i v-if="isUpdating" class="fas fa-spinner animate-spin mr-1"></i>
              อัปเดต Title Stats
            </button>
          </div>
        </div>
        <!-- Bonus Stats Comparison -->
        <div class="mb-6">
          <h4 class="text-sm font-semibold text-indigo-300 mb-3">เปรียบเทียบ Bonus Stats กับ Set Items</h4>
          <div class="text-xs">
            <p class="text-indigo-200">จำนวนไอเทมที่สวมใส่: {{ equippedItems.length }}</p>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
                <h5 class="font-semibold text-indigo-300 text-sm">Bonus Stats (จาก characterPrivate)</h5>
                <div class="text-indigo-200 space-y-1">
                  <p>Stamina: {{ characterPrivateData.bonusStats?.stamina || 0 }}</p>
                  <p>Strength: {{ characterPrivateData.bonusStats?.str || 0 }}</p>
                  <p>Intelligence: {{ characterPrivateData.bonusStats?.int || 0 }}</p>
                  <p>Agility: {{ characterPrivateData.bonusStats?.agi || 0 }}</p>
                  <p>Luck: {{ characterPrivateData.bonusStats?.luck || 0 }}</p>
                  <p>Max HP: {{ characterPrivateData.bonusStats?.maxHp || 0 }}</p>
                  <p>Attack: {{ characterPrivateData.bonusStats?.atk || 0 }}</p>
                  <p>Evasion: {{ characterPrivateData.bonusStats?.evasion || 0 }}</p>
                  <p>Critical: {{ characterPrivateData.bonusStats?.critical || 0 }}</p>
                  <p>Defense: {{ characterPrivateData.bonusStats?.defense || 0 }}</p>
                </div>
              </div>
              <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
                <h5 class="font-semibold text-indigo-300 text-sm">Calculated Bonus Stats (จาก Set Items)</h5>
                <div class="text-indigo-200 space-y-1">
                  <p>Stamina: {{ calculatedBonusStats.stamina }}</p>
                  <p>Strength: {{ calculatedBonusStats.str }}</p>
                  <p>Intelligence: {{ calculatedBonusStats.int }}</p>
                  <p>Agility: {{ calculatedBonusStats.agi }}</p>
                  <p>Luck: {{ calculatedBonusStats.luck }}</p>
                  <p>Max HP: {{ calculatedBonusStats.maxHp }}</p>
                  <p>Attack: {{ calculatedBonusStats.atk }}</p>
                  <p>Evasion: {{ calculatedBonusStats.evasion }}</p>
                  <p>Critical: {{ calculatedBonusStats.critical }}</p>
                  <p>Defense: {{ calculatedBonusStats.defense }}</p>
                </div>
              </div>
            </div>
            <p
              class="mt-3 text-sm"
              :class="isBonusStatsMatching ? 'text-green-300' : 'text-red-300'"
            >
              {{ isBonusStatsMatching ? 'สถานะโบนัสเซ็ตตรงกัน' : 'สถานะโบนัสเซ็ตไม่ตรงกัน' }}
            </p>
            <button
              v-if="!isBonusStatsMatching"
              @click="updateBonusStats"
              :disabled="isUpdating"
              class="bg-indigo-600 hover:bg-indigo-500 text-sm px-4 py-2 rounded-lg transition-colors border border-indigo-700 disabled:opacity-50 mt-2"
            >
              <i v-if="isUpdating" class="fas fa-spinner animate-spin mr-1"></i>
              อัปเดต Bonus Stats
            </button>
          </div>
        </div>
        <!-- Storage Weight Comparison -->
        <div class="mb-6">
          <h4 class="text-sm font-semibold text-indigo-300 mb-3">เปรียบเทียบน้ำหนักในคลัง</h4>
          <div class="text-xs">
            <p class="text-indigo-200">จำนวนไอเทมในคลัง: {{ storageItems.length }}</p>
            <p class="text-indigo-200">จำนวน Invalid Item IDs: {{ invalidStorageItems.length }} (Undefined: {{ invalidStorageItems.filter(id => id === undefined).length }})</p>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
                <h5 class="font-semibold text-indigo-300 text-sm">น้ำหนักในคลัง (จาก storages)</h5>
                <div class="text-indigo-200 space-y-1">
                  <p>น้ำหนัก: {{ storageWeight }}</p>
                  <p>ความจุสูงสุด: {{ storageMaxLoad }}</p>
                </div>
              </div>
              <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
                <h5 class="font-semibold text-indigo-300 text-sm">น้ำหนักที่คำนวณได้ (จากไอเทมในคลัง)</h5>
                <div class="text-indigo-200 space-y-1">
                  <p>น้ำหนัก: {{ calculatedStorageWeight }}</p>
                </div>
              </div>
            </div>
            <p
              class="mt-3 text-sm"
              :class="isStorageWeightMatching ? 'text-green-300' : 'text-red-300'"
            >
              {{ isStorageWeightMatching ? 'น้ำหนักในคลังตรงกัน' : 'น้ำหนักในคลังไม่ตรงกัน' }}
            </p>
            <button
              v-if="!isStorageWeightMatching"
              @click="updateStorageWeight"
              :disabled="isUpdating"
              class="bg-indigo-600 hover:bg-indigo-500 text-sm px-4 py-2 rounded-lg transition-colors border border-indigo-700 disabled:opacity-50 mt-2 mr-2"
            >
              <i v-if="isUpdating" class="fas fa-spinner animate-spin mr-1"></i>
              อัปเดตน้ำหนักในคลัง
            </button>
            <button
              v-if="invalidStorageItems.length > 0"
              @click="removeInvalidStorageItems"
              :disabled="isUpdating"
              class="bg-red-600 hover:bg-red-500 text-sm px-4 py-2 rounded-lg transition-colors border border-red-700 disabled:opacity-50 mt-2"
            >
              <i v-if="isUpdating" class="fas fa-spinner animate-spin mr-1"></i>
              ลบ Invalid Item IDs ในคลัง
            </button>
          </div>
          <!-- Invalid Storage Items -->
          <div v-if="invalidStorageItems.length > 0" class="mt-4">
            <h5 class="font-semibold text-indigo-300 text-sm mb-2">Invalid Item IDs ในคลัง</h5>
            <div class="text-indigo-200 text-xs space-y-2">
              <div
                v-for="(itemId, index) in invalidStorageItems"
                :key="'invalid-storage-' + index"
                class="flex justify-between p-2 bg-gray-700 rounded-md"
              >
                <span>{{ itemId === undefined ? 'undefined' : itemId }}</span>
              </div>
            </div>
          </div>
        </div>
        <!-- Bag Weight Comparison -->
        <div class="mb-6">
          <h4 class="text-sm font-semibold text-indigo-300 mb-3">เปรียบเทียบน้ำหนักในกระเป๋า</h4>
          <div class="text-xs">
            <p class="text-indigo-200">จำนวนไอเทมในกระเป๋า: {{ inventoryItems.length }}</p>
            <p class="text-indigo-200">จำนวน Invalid Item IDs: {{ invalidBagItems.length }} (Undefined: {{ invalidBagItems.filter(id => id === undefined).length }})</p>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
                <h5 class="font-semibold text-indigo-300 text-sm">น้ำหนักในกระเป๋า (จาก bags)</h5>
                <div class="text-indigo-200 space-y-1">
                  <p>น้ำหนัก: {{ currentWeight }}</p>
                  <p>ความจุสูงสุด: {{ maxLoad }}</p>
                </div>
              </div>
              <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
                <h5 class="font-semibold text-indigo-300 text-sm">น้ำหนักที่คำนวณได้ (จากไอเทมในกระเป๋า)</h5>
                <div class="text-indigo-200 space-y-1">
                  <p>น้ำหนัก: {{ calculatedBagWeight }}</p>
                </div>
              </div>
            </div>
            <p
              class="mt-3 text-sm"
              :class="isBagWeightMatching ? 'text-green-300' : 'text-red-300'"
            >
              {{ isBagWeightMatching ? 'น้ำหนักในกระเป๋าตรงกัน' : 'น้ำหนักในกระเป๋าไม่ตรงกัน' }}
            </p>
            <button
              v-if="!isBagWeightMatching"
              @click="updateBagWeight"
              :disabled="isUpdating"
              class="bg-indigo-600 hover:bg-indigo-500 text-sm px-4 py-2 rounded-lg transition-colors border border-indigo-700 disabled:opacity-50 mt-2 mr-2"
            >
              <i v-if="isUpdating" class="fas fa-spinner animate-spin mr-1"></i>
              อัปเดตน้ำหนักในกระเป๋า
            </button>
            <button
              v-if="invalidBagItems.length > 0"
              @click="removeInvalidBagItems"
              :disabled="isUpdating"
              class="bg-red-600 hover:bg-red-500 text-sm px-4 py-2 rounded-lg transition-colors border border-red-700 disabled:opacity-50 mt-2"
            >
              <i v-if="isUpdating" class="fas fa-spinner animate-spin mr-1"></i>
              ลบ Invalid Item IDs ในกระเป๋า
            </button>
          </div>
          <!-- Invalid Bag Items -->
          <div v-if="invalidBagItems.length > 0" class="mt-4">
            <h5 class="font-semibold text-indigo-300 text-sm mb-2">Invalid Item IDs ในกระเป๋า</h5>
            <div class="text-indigo-200 text-xs space-y-2">
              <div
                v-for="(itemId, index) in invalidBagItems"
                :key="'invalid-bag-' + index"
                class="flex justify-between p-2 bg-gray-700 rounded-md"
              >
                <span>{{ itemId === undefined ? 'undefined' : itemId }}</span>
              </div>
            </div>
          </div>
        </div>
        <!-- Individual Stats -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-indigo-200 text-sm">
          <!-- itemStats -->
          <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
            <h4 class="text-sm font-semibold text-indigo-300 mb-2">itemStats</h4>
            <div class="text-xs space-y-1">
              <p>Stamina: {{ characterPrivateData.itemStats?.stamina || 0 }}</p>
              <p>Strength: {{ characterPrivateData.itemStats?.str || 0 }}</p>
              <p>Intelligence: {{ characterPrivateData.itemStats?.int || 0 }}</p>
              <p>Agility: {{ characterPrivateData.itemStats?.agi || 0 }}</p>
              <p>Luck: {{ characterPrivateData.itemStats?.luck || 0 }}</p>
              <p>Max HP: {{ characterPrivateData.itemStats?.maxHp || 0 }}</p>
              <p>Attack: {{ characterPrivateData.itemStats?.atk || 0 }}</p>
              <p>Evasion: {{ characterPrivateData.itemStats?.evasion || 0 }}</p>
              <p>Critical: {{ characterPrivateData.itemStats?.critical || 0 }}</p>
              <p>Defense: {{ characterPrivateData.itemStats?.defense || 0 }}</p>
            </div>
          </div>
          <!-- bonusStats -->
          <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
            <h4 class="text-sm font-semibold text-indigo-300 mb-2">bonusStats</h4>
            <div class="text-xs space-y-1">
              <p>Stamina: {{ characterPrivateData.bonusStats?.stamina || 0 }}</p>
              <p>Strength: {{ characterPrivateData.bonusStats?.str || 0 }}</p>
              <p>Intelligence: {{ characterPrivateData.bonusStats?.int || 0 }}</p>
              <p>Agility: {{ characterPrivateData.bonusStats?.agi || 0 }}</p>
              <p>Luck: {{ characterPrivateData.bonusStats?.luck || 0 }}</p>
              <p>Max HP: {{ characterPrivateData.bonusStats?.maxHp || 0 }}</p>
              <p>Attack: {{ characterPrivateData.bonusStats?.atk || 0 }}</p>
              <p>Evasion: {{ characterPrivateData.bonusStats?.evasion || 0 }}</p>
              <p>Critical: {{ characterPrivateData.bonusStats?.critical || 0 }}</p>
              <p>Defense: {{ characterPrivateData.bonusStats?.defense || 0 }}</p>
            </div>
          </div>
          <!-- stats -->
          <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
            <h4 class="text-sm font-semibold text-indigo-300 mb-2">stats</h4>
            <div class="text-xs space-y-1">
              <p>Stamina: {{ characterPrivateData.stats?.stamina || 0 }}</p>
              <p>Strength: {{ characterPrivateData.stats?.str || 0 }}</p>
              <p>Intelligence: {{ characterPrivateData.stats?.int || 0 }}</p>
              <p>Agility: {{ characterPrivateData.stats?.agi || 0 }}</p>
              <p>Luck: {{ characterPrivateData.stats?.luck || 0 }}</p>
            </div>
          </div>
          <!-- traitStats -->
          <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
            <h4 class="text-sm font-semibold text-indigo-300 mb-2">traitStats</h4>
            <div class="text-xs space-y-1">
              <p>Stamina: {{ characterPrivateData.traitStats?.stamina || 0 }}</p>
              <p>Strength: {{ characterPrivateData.traitStats?.str || 0 }}</p>
              <p>Intelligence: {{ characterPrivateData.traitStats?.int || 0 }}</p>
              <p>Agility: {{ characterPrivateData.traitStats?.agi || 0 }}</p>
              <p>Luck: {{ characterPrivateData.traitStats?.luck || 0 }}</p>
            </div>
          </div>
          <!-- skillStats -->
          <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
            <h4 class="text-sm font-semibold text-indigo-300 mb-2">skillStats</h4>
            <div class="text-xs space-y-1">
              <p>Stamina: {{ characterPrivateData.skillStats?.stamina || 0 }}</p>
              <p>Strength: {{ characterPrivateData.skillStats?.str || 0 }}</p>
              <p>Intelligence: {{ characterPrivateData.skillStats?.int || 0 }}</p>
              <p>Agility: {{ characterPrivateData.skillStats?.agi || 0 }}</p>
              <p>Luck: {{ characterPrivateData.skillStats?.luck || 0 }}</p>
            </div>
          </div>
          <!-- tattooStats -->
          <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
            <h4 class="text-sm font-semibold text-indigo-300 mb-2">tattooStats</h4>
            <div class="text-xs space-y-1">
              <p>Stamina: {{ characterPrivateData.tattooStats?.stamina || 0 }}</p>
              <p>Strength: {{ characterPrivateData.tattooStats?.str || 0 }}</p>
              <p>Intelligence: {{ characterPrivateData.tattooStats?.int || 0 }}</p>
              <p>Agility: {{ characterPrivateData.tattooStats?.agi || 0 }}</p>
              <p>Luck: {{ characterPrivateData.tattooStats?.luck || 0 }}</p>
            </div>
          </div>
          <!-- titleStats -->
          <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
            <h4 class="text-sm font-semibold text-indigo-300 mb-2">titleStats</h4>
            <div class="text-xs space-y-1">
              <p>Stamina: {{ characterPrivateData.titleStats?.stamina || 0 }}</p>
              <p>Strength: {{ characterPrivateData.titleStats?.str || 0 }}</p>
              <p>Intelligence: {{ characterPrivateData.titleStats?.int || 0 }}</p>
              <p>Agility: {{ characterPrivateData.titleStats?.agi || 0 }}</p>
              <p>Luck: {{ characterPrivateData.titleStats?.luck || 0 }}</p>
            </div>
          </div>
          <!-- ghostStats -->
          <div class="bg-gray-600 p-3 rounded-md border border-gray-600">
            <h4 class="text-sm font-semibold text-indigo-300 mb-2">ghostStats</h4>
            <div class="text-xs space-y-1">
              <p>Stamina: {{ characterPrivateData.ghostStats?.stamina || 0 }}</p>
              <p>Strength: {{ characterPrivateData.ghostStats?.str || 0 }}</p>
              <p>Intelligence: {{ characterPrivateData.ghostStats?.int || 0 }}</p>
              <p>Agility: {{ characterPrivateData.ghostStats?.agi || 0 }}</p>
              <p>Luck: {{ characterPrivateData.ghostStats?.luck || 0 }}</p>
              <p>Max HP: {{ characterPrivateData.ghostStats?.maxHp || 0 }}</p>
              <p>Attack: {{ characterPrivateData.ghostStats?.atk || 0 }}</p>
              <p>Evasion: {{ characterPrivateData.ghostStats?.evasion || 0 }}</p>
              <p>Critical: {{ characterPrivateData.ghostStats?.critical || 0 }}</p>
              <p>Defense: {{ characterPrivateData.ghostStats?.defense || 0 }}</p>
            </div>
          </div>
        </div>
      </div>
      <!-- Equipped Items with Tabs -->
      <div class="bg-gray-900 bg-opacity-80 border border-gray-800 p-5 rounded-lg shadow-xl w-full">
        <h3 class="text-xl font-semibold text-indigo-300 mb-3">ไอเทมที่ใส่อยู่</h3>
        <!-- Tab Navigation -->
        <div class="flex border-b border-gray-600 mb-4 w-full">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="activeTab = tab.id"
            class="px-4 py-2 text-sm font-medium transition-colors duration-200"
            :class="activeTab === tab.id ? 'text-indigo-200 border-b-2 border-indigo-500' : 'text-gray-400 hover:text-indigo-300'"
          >
            {{ tab.name }}
          </button>
        </div>
        <div v-if="equippedLoading" class="flex items-center justify-center py-6">
          <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
        </div>
        <div v-else-if="equippedError" class="text-center text-red-300 py-6">
          <p>{{ equippedError }}</p>
        </div>
        <div v-else class="grid gap-4 max-h-96 overflow-y-auto w-full">
          <!-- Tab: Equipment -->
          <div v-if="activeTab === 'equipment'" class="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 w-full">
            <div
              v-for="type in allItemTypes"
              :key="type"
              class="bg-gray-700 rounded-md p-3 flex flex-col items-center border border-gray-600 transition-all duration-200 relative min-w-0"
              :class="{
                'hover:bg-gray-600 hover:border-indigo-400 cursor-pointer': equippedByType[type],
                'opacity-50': !equippedByType[type],
                'border-2 border-indigo-400': selectedItem && selectedItem?.type === type
              }"
              @click="equippedByType[type] ? selectItem(equippedByType[type]) : null"
              @dblclick="equippedByType[type] ? openDetailModal(equippedByType[type]) : null"
            >
              <div class="w-full text-center mb-2 text-xs font-semibold text-indigo-200">{{ type }}</div>
              <div v-if="equippedByType[type]" class="relative">
                <img
                  :src="equippedByType[type]?.img || 'default-item.png'"
                  alt="Item"
                  class="w-16 h-16 rounded-md object-cover border-2 mb-2"
                  :class="[
                    equippedByType[type]?.rarityColors?.borderColor || 'border-indigo-400',
                    equippedByType[type]?.rarityColors?.bgColor || 'bg-gray-700'
                  ]"
                  @error="handleImageError"
                />
                <!-- Rarity display -->
                <div class="absolute bottom-0 left-1/2 -translate-x-1/2 bg-gray-900 bg-opacity-80 border border-gray-800 p-1 rounded flex items-center space-x-1">
                  <div class="rarity-text text-sm">{{ equippedByType[type]?.rarity || 0}}</div>
                  <i class="fas fa-star text-yellow-400 rarity-icon text-sm"></i>
                </div>
              </div>
              <i v-else :class="typeIcons[type] || 'fas fa-ban'" class="text-4xl text-indigo-200 mb-2"></i>
              <p
                v-if="equippedByType[type]"
                class="text-sm text-center break-words whitespace-normal leading-tight"
                :class="equippedByType[type]?.rarityColors?.textColor || 'text-white'"
              >
                {{ equippedByType[type]?.name || 'ไม่ระบุ' }} +{{ equippedByType[type]?.level || '' }}
              </p>
              <p v-if="!equippedByType[type]" class="text-xs text-indigo-200">ยังไม่ได้ใส่</p>
            </div>
          </div>
          <!-- Tab: Mechanical Organs -->
          <div v-if="activeTab === 'mechanical'" class="space-y-6">
            <!-- Group: Skill 1 -->
            <div class="bg-gray-700 rounded-lg p-4">
              <h4 class="text-sm font-semibold text-indigo-300 mb-3">หนังสือวิธีติดตั้งอวัยวะจักรกล</h4>
              <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 w-full">
                <div
                  v-for="name in mechanicalNamesSkill1"
                  :key="name"
                  class="bg-gray-700 rounded-md p-3 flex flex-col items-center border border-gray-600 transition-all duration-200 relative min-w-0"
                  :class="{
                    'hover:bg-gray-600 hover:border-indigo-400 cursor-pointer': mechanicalByName[name],
                    'opacity-50': !mechanicalByName[name],
                    'border-2 border-indigo-400': selectedItem && selectedItem?.name === name
                  }"
                  @click="mechanicalByName[name] ? selectItem(mechanicalByName[name]) : null"
                  @dblclick="mechanicalByName[name] ? openDetailModal(mechanicalByName[name]) : null"
                >
                  <div class="w-full text-center mb-2 text-xs font-semibold text-indigo-200">{{ name }}</div>
                  <div v-if="mechanicalByName[name]" class="relative">
                    <img
                      :src="mechanicalByName[name]?.img || 'default-item.png'"
                      alt="Mechanical Organ"
                      class="w-16 h-16 rounded-md object-cover border-2 mb-2"
                      :class="[
                        mechanicalByName[name]?.rarityColors?.borderColor || 'border-indigo-400',
                        mechanicalByName[name]?.rarityColors?.bgColor || 'bg-gray-700'
                      ]"
                      @error="handleImageError"
                    />
                    <div class="absolute bottom-0 left-1/2 -translate-x-1/2 bg-gray-900 bg-opacity-80 border border-gray-800 p-1 rounded flex items-center space-x-1">
                      <div class="rarity-text text-sm">{{ mechanicalByName[name]?.rarity || 0 }}</div>
                      <i class="fas fa-star text-yellow-400 rarity-icon text-sm"></i>
                    </div>
                  </div>
                  <i v-else class="fas fa-cogs text-4xl text-indigo-200 mb-2"></i>
                  <p
                    v-if="mechanicalByName[name]"
                    class="text-sm text-center break-words whitespace-normal leading-tight"
                    :class="mechanicalByName[name]?.rarityColors?.textColor || 'text-white'"
                  >
                    {{ mechanicalByName[name]?.name || 'ไม่ระบุ' }}
                  </p>
                  <p v-if="!mechanicalByName[name]" class="text-xs text-indigo-200">ยังไม่ได้ติดตั้ง</p>
                </div>
              </div>
            </div>
            <!-- Group: Skill 2 -->
            <div class="bg-gray-700 rounded-lg p-4">
              <h4 class="text-sm font-semibold text-indigo-300 mb-3">หนังสือวิธีติดตั้งอวัยวะจักรกล2</h4>
              <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 w-full">
                <div
                  v-for="name in mechanicalNamesSkill2"
                  :key="name"
                  class="bg-gray-700 rounded-md p-3 flex flex-col items-center border border-gray-600 transition-all duration-200 relative min-w-0"
                  :class="{
                    'hover:bg-gray-600 hover:border-indigo-400 cursor-pointer': mechanicalByName[name],
                    'opacity-50': !mechanicalByName[name],
                    'border-2 border-indigo-400': selectedItem && selectedItem?.name === name
                  }"
                  @click="mechanicalByName[name] ? selectItem(mechanicalByName[name]) : null"
                  @dblclick="mechanicalByName[name] ? openDetailModal(mechanicalByName[name]) : null"
                >
                  <div class="w-full text-center mb-2 text-xs font-semibold text-indigo-200">{{ name }}</div>
                  <div v-if="mechanicalByName[name]" class="relative">
                    <img
                      :src="mechanicalByName[name]?.img || 'default-item.png'"
                      alt="Mechanical Organ"
                      class="w-16 h-16 rounded-md object-cover border-2 mb-2"
                      :class="[
                        mechanicalByName[name]?.rarityColors?.borderColor || 'border-indigo-400',
                        mechanicalByName[name]?.rarityColors?.bgColor || 'bg-gray-700'
                      ]"
                      @error="handleImageError"
                    />
                    <div class="absolute bottom-0 left-1/2 -translate-x-1/2 bg-gray-900 bg-opacity-80 border border-gray-800 p-1 rounded flex items-center space-x-1">
                      <div class="rarity-text text-sm">{{ mechanicalByName[name]?.rarity || 0 }}</div>
                      <i class="fas fa-star text-yellow-400 rarity-icon text-sm"></i>
                    </div>
                  </div>
                  <i v-else class="fas fa-cogs text-4xl text-indigo-200 mb-2"></i>
                  <p
                    v-if="mechanicalByName[name]"
                    class="text-sm text-center break-words whitespace-normal leading-tight"
                    :class="mechanicalByName[name]?.rarityColors?.textColor || 'text-white'"
                  >
                    {{ mechanicalByName[name]?.name || 'ไม่ระบุ' }}
                  </p>
                  <p v-if="!mechanicalByName[name]" class="text-xs text-indigo-200">ยังไม่ได้ติดตั้ง</p>
                </div>
              </div>
            </div>
            <!-- Group: Skill 3 -->
            <div class="bg-gray-700 rounded-lg p-4">
              <h4 class="text-sm font-semibold text-indigo-300 mb-3">หนังสือวิธีติดตั้งอวัยวะจักรกล3</h4>
              <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 w-full">
                <div
                  v-for="name in mechanicalNamesSkill3"
                  :key="name"
                  class="bg-gray-700 rounded-md p-3 flex flex-col items-center border border-gray-600 transition-all duration-200 relative min-w-0"
                  :class="{
                    'hover:bg-gray-600 hover:border-indigo-400 cursor-pointer': mechanicalByName[name],
                    'opacity-50': !mechanicalByName[name],
                    'border-2 border-indigo-400': selectedItem && selectedItem?.name === name
                  }"
                  @click="mechanicalByName[name] ? selectItem(mechanicalByName[name]) : null"
                  @dblclick="mechanicalByName[name] ? openDetailModal(mechanicalByName[name]) : null"
                >
                  <div class="w-full text-center mb-2 text-xs font-semibold text-indigo-200">{{ name }}</div>
                  <div v-if="mechanicalByName[name]" class="relative">
                    <img
                      :src="mechanicalByName[name]?.img || 'default-item.png'"
                      alt="Mechanical Organ"
                      class="w-16 h-16 rounded-md object-cover border-2 mb-2"
                      :class="[
                        mechanicalByName[name]?.rarityColors?.borderColor || 'border-indigo-400',
                        mechanicalByName[name]?.rarityColors?.bgColor || 'bg-gray-700'
                      ]"
                      @error="handleImageError"
                    />
                    <div class="absolute bottom-0 left-1/2 -translate-x-1/2 bg-gray-900 bg-opacity-80 border border-gray-800 p-1 rounded flex items-center space-x-1">
                      <div class="rarity-text text-sm">{{ mechanicalByName[name]?.rarity || 0 }}</div>
                      <i class="fas fa-star text-yellow-400 rarity-icon text-sm"></i>
                    </div>
                  </div>
                  <i v-else class="fas fa-cogs text-4xl text-indigo-200 mb-2"></i>
                  <p
                    v-if="mechanicalByName[name]"
                    class="text-sm text-center break-words whitespace-normal leading-tight"
                    :class="mechanicalByName[name]?.rarityColors?.textColor || 'text-white'"
                  >
                    {{ mechanicalByName[name]?.name || 'ไม่ระบุ' }}
                  </p>
                  <p v-if="!mechanicalByName[name]" class="text-xs text-indigo-200">ยังไม่ได้ติดตั้ง</p>
                </div>
              </div>
            </div>
          </div>
          <!-- Tab: Vehicle -->
          <div v-if="activeTab === 'vehicle'" class="grid grid-cols-1 gap-4">
            <div v-if="equippedVehicle" class="bg-gray-700 rounded-md p-4 flex flex-col items-center border border-gray-600 w-full transition-all duration-200 hover:bg-gray-600 hover:border-indigo-400 cursor-pointer relative min-w-0"
                 @click="selectItem(equippedVehicle)"
                 @dblclick="openDetailModal(equippedVehicle)"
                 :class="{ 'border-2 border-indigo-400': selectedItem && selectedItem?.id === equippedVehicle?.id }">
              <div class="w-full text-center mb-2 text-sm font-semibold text-indigo-200">พาหนะ</div>
              <div class="relative">
                <img
                  :src="equippedVehicle?.img || 'default-item.png'"
                  alt="Vehicle"
                  class="w-20 h-20 rounded-md object-cover border-2 mb-2"
                  :class="[
                    equippedVehicle?.rarityColors?.borderColor || 'border-indigo-400',
                    equippedVehicle?.rarityColors?.bgColor || 'bg-gray-700'
                  ]"
                  @error="handleImageError"
                />
                <div class="absolute bottom-0 left-1/2 -translate-x-1/2 bg-gray-900 bg-opacity-80 border border-gray-800 p-1 rounded flex items-center space-x-1">
                  <div class="rarity-text text-sm">{{ equippedVehicle?.rarity || 'ทั่วไป' }}</div>
                  <i class="fas fa-star text-yellow-400 rarity-icon text-sm"></i>
                </div>
              </div>
              <p
                class="text-sm text-center break-words whitespace-normal leading-tight"
                :class="equippedVehicle?.rarityColors?.textColor || 'text-white'"
              >
                {{ equippedVehicle?.name || 'ไม่ระบุ' }}
              </p>
              <p class="text-xs text-indigo-200 mt-1">ประเภท: {{ equippedVehicle?.type || 'พาหนะ' }}</p>
            </div>
            <div v-else class="bg-gray-700 rounded-md p-4 flex flex-col items-center border border-gray-600 opacity-50">
              <i class="fas fa-motorcycle text-5xl text-indigo-200 mb-2"></i>
              <p class="text-sm text-indigo-200">พาหนะ</p>
              <p class="text-xs text-indigo-200">ยังไม่ได้ติดตั้ง</p>
            </div>
          </div>
          <!-- Tab: Ghosts -->
          <div v-if="activeTab === 'ghosts'" class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 w-full">
            <div
              v-for="(ghost, index) in equippedGhosts"
              :key="index"
              class="bg-gray-700 rounded-md p-3 flex flex-col items-center border border-gray-600 transition-all duration-200 relative min-w-0"
              :class="{
                'hover:bg-gray-600 hover:border-indigo-400 cursor-pointer': ghost,
                'border-2 border-indigo-400': selectedItem && selectedItem?.id === ghost?.id
              }"
              @click="ghost ? selectItem(ghost) : null"
              @dblclick="ghost ? openDetailModal(ghost) : null"
            >
              <div class="w-full text-center mb-2 text-xs font-semibold text-indigo-200">{{ ghost?.subtype || 'วิญญาณ' }}</div>
              <div v-if="ghost" class="relative">
                <img
                  :src="ghost?.img || 'default-item.png'"
                  alt="Ghost"
                  class="w-16 h-16 rounded-md object-cover border-2 mb-2"
                  :class="[
                    ghost?.rarityColors?.borderColor || 'border-indigo-400',
                    ghost?.rarityColors?.bgColor || 'bg-gray-700'
                  ]"
                  @error="handleImageError"
                />
                <div class="absolute bottom-0 left-1/2 -translate-x-1/2 bg-gray-900 bg-opacity-80 border border-gray-800 p-1 rounded flex items-center space-x-1">
                  <div class="rarity-text text-sm">{{ ghost?.rarity || 0 }}</div>
                  <i class="fas fa-star text-yellow-400 rarity-icon text-sm"></i>
                </div>
              </div>
              <i v-else class="fas fa-ghost text-4xl text-indigo-200 mb-2"></i>
              <p
                v-if="ghost"
                class="text-sm text-center break-words whitespace-normal leading-tight"
                :class="ghost?.rarityColors?.textColor || 'text-white'"
              >
                {{ ghost?.name || 'ไม่ระบุ' }}
              </p>
              <p v-if="!ghost" class="text-xs text-indigo-200">ยังไม่ได้อัญเชิญ</p>
            </div>
            <div
              v-for="subtype in ghostSubtypes"
              :key="subtype"
              class="bg-gray-700 rounded-md p-3 flex flex-col items-center border border-gray-600 transition-all duration-200 relative min-w-0 opacity-50"
              :class="{ 'hidden': isGhostSubtypeEquipped(subtype) }"
            >
              <div class="w-full text-center mb-2 text-xs font-semibold text-indigo-200">{{ subtype }}</div>
              <i class="fas fa-ghost text-4xl text-indigo-200 mb-2"></i>
              <p class="text-xs text-indigo-200">ยังไม่ได้อัญเชิญ</p>
            </div>
          </div>
          <!-- Tab: Set Bonuses -->
          <div v-if="activeTab === 'setBonuses'" class="space-y-4">
            <div
              v-for="setInfo in setProgress"
              :key="setInfo.setName"
              class="bg-gray-700 rounded-md p-4 border border-gray-600 transition-all duration-200 hover:border-indigo-400"
            >
              <h4 class="text-sm font-semibold text-indigo-300 mb-3">{{ setInfo.setName }}</h4>
              <p class="text-xs text-indigo-200 mb-2">สวมใส่: {{ setInfo.equippedCount }} / {{ setInfo.totalItems }}</p>
              <div class="w-full bg-gray-700 rounded-full h-2 mt-1">
                <div class="bg-indigo-500 h-2 rounded-full" :style="{ width: `${(setInfo.equippedCount / setInfo.totalItems) * 100}%` }"></div>
              </div>
              <div class="mt-3">
                <h5 class="text-xs font-semibold text-indigo-300 mb-1">โบนัสปัจจุบัน</h5>
                <div class="grid grid-cols-2 gap-2">
                  <div v-for="stat in statOrder" :key="stat" class="flex justify-between text-xs">
                    <span>{{ statMeta[stat]?.label || stat }}</span>
                    <span :class="getStat(setInfo.currentBonus, stat) > 0 ? 'text-green-300' : 'text-gray-400'">{{ formatBonus(getStat(setInfo.currentBonus, stat)) }}</span>
                  </div>
                </div>
              </div>
              <div class="mt-3" v-if="setInfo.nextBonuses.length">
                <h5 class="text-xs font-semibold text-indigo-300 mb-1">โบนัสต่อไป</h5>
                <div v-for="(next, idx) in setInfo.nextBonuses" :key="idx" class="mt-2 border-t border-gray-600 pt-2">
                  <p class="text-xs text-yellow-300">ต้องการ {{ next.required }} ชิ้น (เหลือ {{ next.required - setInfo.equippedCount }} ชิ้น)</p>
                  <div class="grid grid-cols-2 gap-2 mt-1">
                    <div v-for="stat in statOrder" :key="stat" class="flex justify-between text-xs">
                      <span>{{ statMeta[stat]?.label || stat }}</span>
                      <span :class="getStat(next.bonus, stat) > 0 ? 'text-yellow-300' : 'text-gray-400'">{{ formatBonus(getStat(next.bonus, stat)) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Missing Equipment Slots -->
        <div v-if="missingItemTypes.length > 0" class="mt-4 p-3 bg-red-900 bg-opacity-50 border border-red-600 rounded-lg">
          <h4 class="text-sm font-semibold text-red-300 mb-2">ช่องที่ยังไม่ได้ใส่</h4>
          <div class="flex flex-wrap gap-2">
            <span v-for="type in missingItemTypes" :key="type" class="bg-red-800 px-2 py-1 rounded text-xs text-red-200">
              {{ type }}
            </span>
          </div>
        </div>
      </div>
      <!-- Inventory -->
      <div class="bg-gray-900 bg-opacity-80 border border-gray-800 p-5 rounded-lg shadow-xl">
        <h3 class="text-xl font-semibold text-indigo-300 mb-3">กระเป๋า</h3>
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-lg font-semibold text-indigo-200 flex items-center gap-2">
            <i class="fas fa-suitcase"></i> กระเป๋า ({{ inventoryItems.length }} ไอเทม)
          </h2>
        </div>
        <div class="flex justify-between items-center bg-gray-700 rounded-md p-2 border border-gray-600 mb-4">
          <div class="flex items-center space-x-1">
            <span class="text-sm text-indigo-200">น้ำหนัก: {{ currentWeight }} / {{ maxLoad }}</span>
            <i class="fas fa-weight-hanging text-indigo-400 text-xs"></i>
          </div>
        </div>
        <div v-if="inventoryLoading" class="flex items-center justify-center py-6">
          <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
        </div>
        <div v-else-if="inventoryError" class="text-center text-red-300 py-6">
          <p>{{ inventoryError }}</p>
        </div>
        <div v-else-if="inventoryItems.length > 0" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 max-h-64 overflow-y-auto w-full">
          <div
            v-for="item in inventoryItems"
            :key="item.id"
            @click="selectItem(item)"
            @dblclick="openDetailModal(item)"
            class="bg-gray-700 rounded-md p-3 flex flex-col items-center border border-gray-600 transition-all duration-200 hover:bg-gray-600 hover:border-indigo-400 cursor-pointer relative"
            :class="{ 'border-2 border-indigo-400': selectedItem && selectedItem.id === item.id }"
          >
            <div class="relative">
              <img
                :src="item.img || 'default-item.png'"
                alt="Item"
                class="w-16 h-16 rounded-md object-cover border-2 mb-2"
                :class="[
                  item.rarityColors?.borderColor || 'border-indigo-400',
                  item.rarityColors?.bgColor || 'bg-gray-700'
                ]"
                @error="handleImageError"
              />
              <div class="absolute bottom-0 left-1/2 -translate-x-1/2 bg-gray-900 bg-opacity-80 border border-gray-800 p-1 rounded flex items-center space-x-1">
                <div class="rarity-text text-sm">{{ item.rarity }}</div>
                <i class="fas fa-star text-yellow-400 rarity-icon text-sm"></i>
              </div>
            </div>
            <div class="flex flex-col items-center">
              <p
                class="text-sm text-center flex items-center gap-1 break-words whitespace-normal leading-tight"
                :class="item.rarityColors?.textColor || 'text-white'"
              >
                {{ item.name }}
                <i v-if="item.transfer" class="fa-solid fa-arrow-right-arrow-left text-green-400 text-xs"></i>
              </p>
              <p class="text-xs text-indigo-200">จำนวน: {{ item.quantity }}</p>
            </div>
          </div>
        </div>
        <div v-else class="text-center text-indigo-200 py-6">
          <p>ไม่มีไอเทมในกระเป๋า</p>
        </div>
      </div>
      <!-- Storage -->
      <div class="bg-gray-900 bg-opacity-80 border border-gray-800 p-5 rounded-lg shadow-xl">
        <h3 class="text-xl font-semibold text-indigo-300 mb-3">คลังเก็บของ</h3>
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-lg font-semibold text-indigo-200 flex items-center gap-2">
            <i class="fas fa-warehouse"></i> คลังเก็บของ ({{ storageItems.length }} ไอเทม)
          </h2>
        </div>
        <p class="text-sm text-gray-400 mb-4">น้ำหนัก: {{ storageWeight }} / {{ storageMaxLoad }}</p>
        <div v-if="storageLoading" class="flex items-center justify-center py-6">
          <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
        </div>
        <div v-else-if="storageError" class="text-center text-red-300 py-6">
          <p>{{ storageError }}</p>
        </div>
        <div v-else-if="storageItems.length > 0" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 max-h-64 overflow-y-auto">
          <div
            v-for="item in storageItems"
            :key="item.id"
            @click="selectItem(item)"
            @dblclick="openDetailModal(item)"
            class="bg-gray-700 rounded-md p-3 flex flex-col items-center border border-gray-600 transition-all duration-200 hover:bg-gray-600 hover:border-indigo-400 cursor-pointer relative"
            :class="{ 'border-2 border-indigo-400': selectedItem && selectedItem.id === item.id }"
          >
            <div class="relative">
              <img
                :src="item.img || 'default-item.png'"
                alt="Item"
                class="w-16 h-16 rounded-md object-cover border-2 mb-2"
                :class="[
                  item.rarityColors?.borderColor || 'border-indigo-400',
                  item.rarityColors?.bgColor || 'bg-gray-700'
                ]"
                @error="handleImageError"
              />
              <div class="absolute bottom-0 left-1/2 -translate-x-1/2 bg-gray-900 bg-opacity-80 border border-gray-800 p-1 rounded flex items-center space-x-1">
                <div class="rarity-text text-sm">{{ item.rarity }}</div>
                <i class="fas fa-star text-yellow-400 rarity-icon text-sm"></i>
              </div>
            </div>
            <div class="flex flex-col items-center">
              <p
                class="text-sm text-center flex items-center gap-1 break-words whitespace-normal leading-tight"
                :class="item.rarityColors?.textColor || 'text-white'"
              >
                {{ item.name }}
                <i v-if="item.transfer" class="fa-solid fa-arrow-right-arrow-left text-green-400 text-xs"></i>
              </p>
              <p class="text-xs text-indigo-200">จำนวน: {{ item.quantity }}</p>
            </div>
          </div>
        </div>
        <div v-else class="text-center text-indigo-200 py-6">
          <p>ไม่มีไอเทมในคลัง</p>
        </div>
      </div>
      <!-- Item Details -->
      <div
        v-if="selectedItem"
        class="bg-gray-900 bg-opacity-80 border border-gray-800 p-5 rounded-lg shadow-xl"
      >
        <h3 class="text-xl font-semibold text-indigo-300 mb-3">รายละเอียดไอเทม</h3>
        <div class="space-y-3">
          <div>
            <h4 class="text-sm font-semibold text-indigo-200 border-b border-gray-600 pb-1 mb-2">ข้อมูลทั่วไป</h4>
            <div class="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 text-xs">
              <p class="text-indigo-200">น้ำหนัก: {{ selectedItem.weight || 0 }} กก.</p>
              <p class="text-indigo-200">เลเวลที่ต้องใช้: {{ selectedItem.requiredLevel || 1 }}</p>
              <p class="text-indigo-200">ราคา: {{ selectedItem.price || 0 }}</p>
            </div>
            <div v-if="selectedItem.description" class="mt-2">
              <p class="text-indigo-200">คำอธิบาย: {{ selectedItem.description }}</p>
            </div>
          </div>
          <div v-if="selectedItem.decomposable && selectedItem.decomposeList?.length > 0">
            <h4 class="text-sm font-semibold text-indigo-200 border-b border-gray-600 pb-1 mb-2">ผลลัพธ์ที่อาจได้รับจากการแยกชิ้นส่วน</h4>
            <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <p v-for="(decomposeItem, index) in selectedItem.decomposeList" :key="index" class="text-xs text-indigo-200">
                {{ decomposeItem.itemName }}: {{ decomposeItem.chance }}
              </p>
            </div>
          </div>
          <div v-if="selectedItem.stats && Object.values(selectedItem.stats).some(val => val > 0)">
            <h4 class="text-sm font-semibold text-indigo-200 border-b border-gray-600 pb-1 mb-2">ค่าสถานะ</h4>
            <div class="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 text-xs">
              <p v-if="selectedItem.stats.stamina" class="text-indigo-200">Stamina: {{ selectedItem.stats.stamina }}</p>
              <p v-if="selectedItem.stats.str" class="text-indigo-200">Strength: {{ selectedItem.stats.str }}</p>
              <p v-if="selectedItem.stats.int" class="text-indigo-200">Intelligence: {{ selectedItem.stats.int }}</p>
              <p v-if="selectedItem.stats.agi" class="text-indigo-200">Agility: {{ selectedItem.stats.agi }}</p>
              <p v-if="selectedItem.stats.luck" class="text-indigo-200">Luck: {{ selectedItem.stats.luck }}</p>
              <p v-if="selectedItem.stats.maxHp" class="text-indigo-200">Max HP: {{ selectedItem.stats.maxHp }}</p>
              <p v-if="selectedItem.stats.atk" class="text-indigo-200">Attack: {{ selectedItem.stats.atk }}</p>
              <p v-if="selectedItem.stats.evasion" class="text-indigo-200">Evasion: {{ selectedItem.stats.evasion }}</p>
              <p v-if="selectedItem.stats.critical" class="text-indigo-200">Critical: {{ selectedItem.stats.critical }}</p>
              <p v-if="selectedItem.stats.defense" class="text-indigo-200">Defense: {{ selectedItem.stats.defense }}</p>
            </div>
          </div>
          <div v-if="selectedItem.type === 'อาหาร'">
            <h4 class="text-sm font-semibold text-indigo-200 border-b border-gray-600 pb-1 mb-2">ข้อมูลอาหาร</h4>
            <div class="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 text-xs">
              <p class="text-indigo-200">ระดับอาหาร: {{ selectedItem.foodLevel || 0 }}</p>
              <p class="text-indigo-200">ระดับความสุข: {{ selectedItem.happinessLevel || 0 }}</p>
            </div>
          </div>
        </div>
      </div>
       <!-- Header for Logs -->
    <div class="header w-full bg-gray-900 p-3 border-b border-gray-800 mb-6">
      <div class="filter-controls flex flex-col sm:flex-row gap-4 justify-center">
        <select v-model="filterAction" @change="applyFilters" class="filter-select bg-gray-700 border border-gray-600 text-indigo-200 text-sm py-2 px-3 rounded-md transition-all duration-200 hover:bg-gray-600 hover:border-indigo-400">
          <option value="">ทุกการกระทำ</option>
          <option value="pvp">การต่อสู้ PvP</option>
          <option value="upgrade_stats">อัปเกรดสเตตัส</option>
          <option value="attack_zombie">โจมตีซอมบี้</option>
          <option value="explore">ผจญภัย</option>
          <option value="flee_fight">หนีการต่อสู้</option>
          <option value="claim_rewards">รับรางวัล</option>
          <option value="go_home">กลับบ้าน</option>
          <option value="add_to_bag">ใส่ไอเทมลงกระเป๋า</option>
          <option value="discard_item">ทิ้งไอเทม</option>
          <option value="equip_item">ใส่ไอเทม</option>
          <option value="unequip_item">ถอนไอเทม</option>
          <option value="move_item_to_storage">ย้ายไอเทมไปคลัง</option>
          <option value="move_item_to_bag">ย้ายไอเทมไปกระเป๋า</option>
          <option value="change_trait">เปลี่ยนลักษณะนิสัย</option>
          <option value="learn_skill">เรียนรู้ทักษะ</option>
          <option value="tattoo_skin">สักลาย</option>
          <option value="unlock_title">ปลดล็อกฉายา</option>
          <option value="use_title">ใช้งานฉายา</option>
          <option value="heal_health">รักษาเลือด</option>
          <option value="heal_infection">รักษาการติดเชื้อ</option>
          <option value="sell_item">ขายไอเทม</option>
          <option value="buy_item">ซื้อไอเทม</option>
          <option value="buy_food">ซื้ออาหาร</option>
          <option value="unlock_room">ปลดล็อกห้อง</option>
          <option value="assassinate_criminal">ลอบสังหารอาชญากร</option>
          <option value="punish">ลงโทษ</option>
          <option value="reborn">เกิดใหม่</option>
          <option value="upgrade_storage">อัปเกรดคลัง</option>
          <option value="upgrade_bag">อัปเกรดกระเป๋า</option>
          <option value="move_room">ย้ายห้อง</option>
          <option value="sell_items">ขายไอเทม</option>
          <option value="energyIncrease">เพิ่มพลังงาน</option>
          <option value="claim_mysterious_item">สุ่มรับไอเทมคลังปริศนา</option>
          <option value="claim_mysterious_item_with_diamonds">กินอาหาร</option>
          <option value="consume_food">สุ่มรับไอเทมกล่องปริศนาด้วยแคช</option>
          <option value="claim_mysterious_item_free">สุ่มรับไอเทมกล่องปริศนาด้วยฟรี</option>
          <option value="healReset">รีเซ็ตฮีล</option>
        </select>
        <div class="search-container relative flex-1 max-w-xs">
          <input
            v-model="searchQuery"
            @input="applyFilters"
            placeholder="ค้นหา..."
            class="search-input w-full bg-gray-700 border border-gray-600 text-indigo-200 text-sm py-2 pl-3 pr-8 rounded-md transition-all duration-200 hover:bg-gray-600 hover:border-indigo-400"
          />
          <i class="fas fa-search search-icon absolute right-3 top-1/2 transform -translate-y-1/2 text-indigo-400 text-sm"></i>
        </div>
      </div>
    </div>
      <!-- Logs Section -->
      <div class="bg-gray-900 bg-opacity-80 border border-gray-800 p-5 rounded-lg shadow-xl">
        <h3 class="text-xl font-semibold text-indigo-300 mb-3">ประวัติการกระทำล่าสุด</h3>
        <div class="logs-list w-full mx-auto">
          <div v-if="logsLoading" class="loading-state bg-gray-700 border border-gray-600 p-4 rounded-lg text-center text-indigo-200 text-sm">
            <p>กำลังโหลด...</p>
          </div>
          <div v-else-if="filteredLogs.length === 0" class="empty-state bg-gray-700 border border-gray-600 p-4 rounded-lg text-center text-indigo-200 text-sm">
            <p>ไม่พบประวัติการกระทำ</p>
          </div>
          <div
            v-else
            v-for="log in filteredLogs"
            :key="log.id"
            :id="`log-${log.id}`"
            class="log-card bg-gray-700 border border-gray-600 rounded-lg p-4 mb-4 shadow-xl transition-all duration-200 hover:bg-gray-600 hover:border-indigo-400"
            :class="{ 'border-l-4 border-indigo-600': log.success, 'border-l-4 border-red-600': !log.success }"
          >
            <div class="log-header flex justify-between items-center mb-2">
              <span class="log-action text-sm font-semibold text-indigo-300">{{ getActionName(log.action) }}</span>
              <span class="log-timestamp text-xs text-indigo-200">{{ formatTimestamp(log.timestamp) }}</span>
            </div>
            <p class="log-message text-sm text-white mb-2">{{ log.displayMessage }}</p>
            <!-- Warning for High Infection Level, Low Energy, or Low Cash -->
            <div
              v-if="checkWarnings(log)"
              class="warning-message mt-2 p-2 bg-red-600 text-red-100 rounded-md text-sm"
            >
              {{ checkWarnings(log).message }}
            </div>
          </div>
          <div v-if="hasMoreLogs && !logsLoading" class="text-center mt-4">
            <button
              @click="loadMoreLogs"
              class="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold py-2 px-4 rounded-md transition-all duration-200"
            >
              โหลดเพิ่ม
            </button>
          </div>
        </div>
      </div>
      <!-- Back Button -->
      <div class="text-center mt-4">
        <button
          @click="goBack"
          class="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold py-2 px-4 rounded-md transition-all duration-200"
        >
          <i class="fas fa-arrow-left mr-2 text-xs"></i> กลับไปแดชบอร์ด
        </button>
      </div>
    </div>
    <div v-else class="text-center text-indigo-200 py-6">
      <p>ไม่พบข้อมูลตัวละคร</p>
    </div>
    <!-- Stat and Power Modals -->
    <div v-if="toggleModal === 'StatDetailsModal'" class="fixed inset-0 flex items-center justify-center z-50">
      <div class="fixed inset-0 bg-gray-900 bg-opacity-70" @click="toggleModal = ''"></div>
      <StatDetailsModal :statName="selectedStat" :breakdown="statBreakdown" @close="toggleModal = ''" />
    </div>
    <div v-if="toggleModal === 'PowerDetailsModal'" class="fixed inset-0 flex items-center justify-center z-50">
      <div class="fixed inset-0 bg-gray-900 bg-opacity-70" @click="toggleModal = ''"></div>
      <PowerDetailsModal :powerName="selectedPower" :breakdown="powerBreakdown" @close="toggleModal = ''" />
    </div>
    <!-- Detail Item Modal -->
    <detail-item-modal
      v-if="showDetailModal"
      :itemId="selectedItem ? selectedItem.id : ''"
      :item="selectedItem"
      @close="showDetailModal = false"
    />
  </div>
</template>
<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { projectFirestore, FieldValue } from '@/firebase/config';
import gameItemsData from '@/data/gameItems.json';
import firebase from 'firebase/app';
import StatDetailsModal from '@/components/character/StatDetailsModal.vue';
import PowerDetailsModal from '@/components/character/PowerDetailsModal.vue';
import DetailItemModal from '@/components/DetailItemModal.vue';
const LEVEL_EXP_TABLE = [
  { level: 1, expRequired: 50 },
  { level: 2, expRequired: 100 },
  { level: 3, expRequired: 150 },
  { level: 4, expRequired: 200 },
  { level: 5, expRequired: 250 },
  { level: 6, expRequired: 300 },
  { level: 7, expRequired: 350 },
  { level: 8, expRequired: 400 },
  { level: 9, expRequired: 450 },
  { level: 10, expRequired: 500 },
  { level: 11, expRequired: 600 },
  { level: 12, expRequired: 700 },
  { level: 13, expRequired: 800 },
  { level: 14, expRequired: 900 },
  { level: 15, expRequired: 1000 },
  { level: 16, expRequired: 1150 },
  { level: 17, expRequired: 1300 },
  { level: 18, expRequired: 1450 },
  { level: 19, expRequired: 1600 },
  { level: 20, expRequired: 1750 },
  { level: 21, expRequired: 1950 },
  { level: 22, expRequired: 2150 },
  { level: 23, expRequired: 2350 },
  { level: 24, expRequired: 2550 },
  { level: 25, expRequired: 2750 },
  { level: 26, expRequired: 3050 },
  { level: 27, expRequired: 3350 },
  { level: 28, expRequired: 3650 },
  { level: 29, expRequired: 3950 },
];
LEVEL_EXP_TABLE.forEach((levelData, index) => {
  levelData.totalExp = index === 0 ? 0 : LEVEL_EXP_TABLE[index - 1].totalExp + LEVEL_EXP_TABLE[index - 1].expRequired;
});
const titles = {
  'เด็กเก็บขยะ': {
    stats: { stamina: 3, str: 0, int: 0, agi: 0 },
    renown: 300,
    goldCost: 300,
    silverCost: 3000,
    levelRequired: 3
  },
  'เด็กส่งของ': {
    stats: { stamina: 3, str: 3, int: 0, agi: 0 },
    renown: 600,
    goldCost: 600,
    silverCost: 6000,
    levelRequired: 6,
    requiredTitle: 'เด็กเก็บขยะ'
  },
  'คนรับใช้': {
    stats: { stamina: 3, str: 3, int: 3, agi: 0 },
    renown: 900,
    goldCost: 900,
    silverCost: 9000,
    levelRequired: 9,
    requiredTitle: 'เด็กส่งของ'
  },
  'คนสอดรู้': {
    stats: { stamina: 3, str: 3, int: 3, agi: 3 },
    renown: 1200,
    goldCost: 1200,
    silverCost: 12000,
    levelRequired: 12,
    requiredTitle: 'คนรับใช้'
  },
  'ช่างซ่อม': {
    stats: { stamina: 6, str: 3, int: 3, agi: 3 },
    renown: 1500,
    goldCost: 1500,
    silverCost: 15000,
    levelRequired: 15,
    requiredTitle: 'คนสอดรู้'
  },
  'นักสำรวจ': {
    stats: { stamina: 6, str: 6, int: 3, agi: 3 },
    renown: 1800,
    goldCost: 1800,
    silverCost: 18000,
    levelRequired: 18,
    requiredTitle: 'ช่างซ่อม'
  },
  'นักวางแผน': {
    stats: { stamina: 6, str: 6, int: 6, agi: 6 },
    renown: 2100,
    goldCost: 2100,
    silverCost: 21000,
    levelRequired: 21,
    requiredTitle: 'นักสำรวจ'
  },
  'ผู้มีประสบการณ์': {
    stats: { stamina: 9, str: 6, int: 6, agi: 6 },
    renown: 2400,
    goldCost: 2400,
    silverCost: 24000,
    levelRequired: 24,
    requiredTitle: 'นักวางแผน'
  },
  'ผู้มีอิทธิพล': {
    stats: { stamina: 9, str: 9, int: 9, agi: 6 },
    renown: 2700,
    goldCost: 2700,
    silverCost: 27000,
    levelRequired: 27,
    requiredTitle: 'ผู้มีประสบการณ์'
  },
  'เจ้าพ่อ': {
    stats: { stamina: 10, str: 10, int: 10, agi: 10 },
    renown: 3000,
    goldCost: 3000,
    silverCost: 30000,
    levelRequired: 30,
    requiredTitle: 'ผู้มีอิทธิพล'
  },
  'คนครึ่งหุ่นยนต์': {
    stats: { stamina: 13, str: 10, int: 10, agi: 10 },
    renown: 3300,
    goldCost: 3500,
    silverCost: 40000,
    levelRequired: 30,
    requiredTitle: 'เจ้าพ่อ'
  },
  'คนเล่นของ': {
    stats: { stamina: 13, str: 13, int: 10, agi: 10 },
    renown: 3600,
    goldCost: 4000,
    silverCost: 50000,
    levelRequired: 30,
    requiredTitle: 'คนครึ่งหุ่นยนต์'
  },
  'คนประหลาด': {
    stats: { stamina: 13, str: 13, int: 13, agi: 10 },
    renown: 3900,
    goldCost: 4500,
    silverCost: 60000,
    levelRequired: 30,
    requiredTitle: 'คนเล่นของ'
  },
  'มนุษย์เหล็ก': {
    stats: { stamina: 13, str: 13, int: 13, agi: 13 },
    renown: 4200,
    goldCost: 5000,
    silverCost: 70000,
    levelRequired: 30,
    requiredTitle: 'คนประหลาด'
  },
  'นักรบเหล็ก': {
    stats: { stamina: 16, str: 13, int: 13, agi: 13 },
    renown: 4500,
    goldCost: 5500,
    silverCost: 70000,
    levelRequired: 30,
    requiredTitle: 'มนุษย์เหล็ก'
  },
  'ผู้ทรงพลัง': {
    stats: { stamina: 16, str: 16, int: 13, agi: 13 },
    renown: 4800,
    goldCost: 6000,
    silverCost: 90000,
    levelRequired: 30,
    requiredTitle: 'นักรบเหล็ก'
  }
};
const setConfigs = {
  'เซ็ตทหาร': {
    items: [
      'หมวกทหาร', 'เสื้อกองทัพบก', 'เสื้อคลุมทหาร', 'ถุงมือทหาร',
      'กางเกงทหาร', 'รองเท้าคอมแบท', 'เข็มขัดทหาร', 'แท่งเหล็กปลายมีด'
    ],
    bonuses: {
      4: { critical: 4, evasion: 4, atk: 20 },
      8: { critical: 10, evasion: 10, atk: 50 }
    }
  },
  'เซ็ตนักดับเพลิง': {
    items: [
      'หมวกนิรภัย', 'เสื้อนักดับเพลิง', 'เสื้อคลุมนักดับเพลิง', 'ถุงมือนักดับเพลิง',
      'กางเกงนักดับเพลิง', 'รองเท้านักดับเพลิง', 'เข็มขัดนักดับเพลิง', 'เลื่อยไฟฟ้า'
    ],
    bonuses: {
      4: { maxHp: 1000, critical: 4, defense: 20 },
      8: { maxHp: 2500, critical: 10, defense: 50, atk: 10 }
    }
  },
  'เซ็ตตัวตลก': {
    items: ['ถุงมือตัวตลก', 'เสื้อตัวตลก', 'กางเกงตัวตลก', 'หมวกตัวตลก', 'รองเท้าตัวตลก'],
    bonuses: {
      2: { atk: 15, critical: 10 },
      5: { atk: 40, critical: 20 }
    }
  },
  'เซ็ตเทควันโด': {
    items: ['เสื้อเทควันโด', 'เสื้อคลุมเทควันโด', 'กางเกงเทควันโด'],
    bonuses: {
      3: { evasion: 20, atk: 30 }
    }
  },
  'เซ็นนักรบไทย': {
    items: ['เกราะนักรบไทย', 'ปลอกแขนนักรบไทย', 'สนับแข้งนักรบไทย','หมวกนักรบไทย','หมวกอัศวิน','ดาบ'],
    bonuses: {
      3: { critical: 3, evasion: 3, atk: 20},
      4: { critical: 6, evasion: 6, atk: 30},
      5: { critical: 10, evasion: 10, atk: 40}
    }
  }
};
export default {
  name: 'AdminCharacterView',
  components: {
    StatDetailsModal,
    PowerDetailsModal,
    DetailItemModal,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const userId = ref(route.params.userId || '');
    const characterData = ref(null);
    const characterPrivateData = ref(null);
    const userData = ref(null);
    const loading = ref(true);
    const error = ref(null);
    const toggleModal = ref('');
    const selectedStat = ref(null);
    const selectedPower = ref(null);
    const selectedItem = ref(null);
    const showDetailModal = ref(false);
    const isUpdating = ref(false);
    const newLevel = ref(null);
    const addSilver = ref(null);
    const addGold = ref(null);
    const addDiamond = ref(null);
    const currentTitle = ref(null);
    const itemNames = ref([]);
    const ghostItems = ref([]);
    // Equipped Items
    const activeTab = ref('equipment');
    const tabs = ref([
      { id: 'equipment', name: 'อุปกรณ์' },
      { id: 'mechanical', name: 'อวัยวะจักรกล' },
      { id: 'ghosts', name: 'วิญญาณ' },
      { id: 'vehicle', name: 'พาหนะ' },
      { id: 'setBonuses', name: 'โบนัสเซ็ต' },
    ]);
    const equippedItems = ref([]);
    const equippedVehicle = ref(null);
    const equippedMechanicalOrgans = ref([]);
    const equippedGhosts = ref([]);
    const equippedItemTypes = ref([]);
    const equippedLoading = ref(true);
    const equippedError = ref(null);
    const allItemTypes = [
      'หมวก', 'แว่นตา', 'เสื้อ', 'เสื้อคลุม', 'เกราะ', 'ปลอกแขน', 'ถุงมือ', 'กางเกง', 'สนับแข้ง', 'รองเท้า',
      'เข็มขัด', 'สร้อยคอ', 'แหวน', 'อาวุธ', 'อาวุธสำรอง', 'ตะกรุด', 'ผ้ายันต์', 'สายสิญจน์',
    ];
    const mechanicalNamesSkill1 = [
      'ดวงตาจักรกล', 'สมองจักรกล', 'หัวใจจักรกล', 'ซี่โครงจักรกล', 'แขนจักรกล', 'หมัดจักรกล', 'ขาจักรกล',
    ];
    const mechanicalNamesSkill2 = [
      'ลำตัวจักรกล', 'สะโพกจักรกล', 'ตาหลังจักรกล', 'กรามจักรกล', 'ไหล่จักรกล', 'จมูกจักรกล', 'หูจักรกล',
    ];
    const mechanicalNamesSkill3 = [
      'ปีกจักรกล', 'หนวดจักรกล', 'เขาจักรกล',
    ];
    const ghostSubtypes = ref([
      'วิญญาณเฝ้าห้อง',
      'วิญญาณค้นหาผู้เล่น',
      'วิญญาณผู้พิทักษ์'
    ]);
    const typeIcons = {
      'หมวก': 'fas fa-hat-wizard',
      'แว่นตา': 'fas fa-glasses',
      'เสื้อ': 'fas fa-tshirt',
      'เสื้อคลุม': 'fas fa-user-tie',
      'เกราะ': 'fas fa-shield-alt',
      'ปลอกแขน': 'fas fa-hand-paper',
      'ถุงมือ': 'fas fa-mitten',
      'กางเกง': 'fas fa-socks',
      'สนับแข้ง': 'fas fa-shoe-prints',
      'รองเท้า': 'fas fa-boot',
      'เข็มขัด': 'fas fa-belt-buckle',
      'สร้อยคอ': 'fas fa-gem',
      'แหวน': 'fas fa-ring',
      'อาวุธ': 'fas fa-sword',
      'อาวุธสำรอง': 'fas fa-shield',
      'ตะกรุด': 'fas fa-scroll',
      'ผ้ายันต์': 'fas fa-pray',
      'สายสิญจน์': 'fas fa-prayer-beads',
    };
    const statOrder = ['stamina', 'str', 'int', 'agi', 'luck', 'maxHp', 'atk', 'evasion', 'critical', 'defense'];
    const statMeta = {
      stamina: { label: 'Stamina', icon: 'fas fa-heart' },
      str: { label: 'STR', icon: 'fas fa-dumbbell' },
      int: { label: 'INT', icon: 'fas fa-brain' },
      agi: { label: 'AGI', icon: 'fas fa-running' },
      luck: { label: 'Luck', icon: 'fas fa-clover' },
      maxHp: { label: 'Max HP', icon: 'fas fa-heartbeat' },
      atk: { label: 'ATK', icon: 'fas fa-hand-fist' },
      evasion: { label: 'Evasion', icon: 'fas fa-person-running' },
      critical: { label: 'Critical', icon: 'fas fa-burst' },
      defense: { label: 'Defense', icon: 'fas fa-shield-alt' },
    };
    const equippedByType = computed(() => {
      const map = {};
      equippedItems.value.forEach(item => {
        if (item && item.type) {
          map[item.type] = item;
        }
      });
      return map;
    });
    const mechanicalByName = computed(() => {
      const map = {};
      equippedMechanicalOrgans.value.forEach(item => {
        if (item && item.name) {
          map[item.name] = item;
        }
      });
      return map;
    });
    const isGhostSubtypeEquipped = subtype => {
      return equippedGhosts.value.some(g => g.subtype === subtype);
    };
    const getStat = (obj, key) => {
      return obj ? obj[key] || 0 : 0;
    };
    const formatBonus = num => {
      return num > 0 ? `+${num}` : num;
    };
    const setProgress = computed(() => {
      const progress = [];
      const equippedNames = equippedItems.value.map(item => item.name).filter(Boolean);
      for (const [setName, config] of Object.entries(setConfigs)) {
        const equippedInSet = config.items.filter(name => equippedNames.includes(name)).length;
        if (equippedInSet === 0) continue;
        const bonusKeys = Object.keys(config.bonuses).map(Number).sort((a, b) => b - a);
        let currentBonus = {};
        const nextBonuses = [];
        for (const required of bonusKeys) {
          if (equippedInSet >= required) {
            currentBonus = config.bonuses[required];
            break;
          }
        }
        for (const required of bonusKeys.filter(r => r > equippedInSet)) {
          nextBonuses.push({ required, bonus: config.bonuses[required] });
        }
        progress.push({
          setName,
          equippedCount: equippedInSet,
          totalItems: config.items.length,
          currentBonus,
          nextBonuses
        });
      }
      return progress;
    });
    const missingItemTypes = computed(() => allItemTypes.filter(type => !equippedItemTypes.value.includes(type)));
    // Inventory
    const inventoryItems = ref([]);
    const currentWeight = ref(0);
    const maxLoad = ref(100);
    const inventoryLoading = ref(true);
    const inventoryError = ref(null);
    const inventorySelectedType = ref('');
    const inventorySearchQuery = ref('');
    const invalidBagItems = ref([]);
    // Storage
    const storageItems = ref([]);
    const storageWeight = ref(0);
    const storageMaxLoad = ref(100);
    const storageLoading = ref(true);
    const storageError = ref(null);
    const storageSelectedType = ref('');
    const storageSearchQuery = ref('');
    const invalidStorageItems = ref([]);
    // Tattoos and Skills
    const tattoos = ref([]);
    const skills = ref([]);
    // Sorting
    const isSorting = ref(false);
    const showSortModal = ref(false);
    const sortContext = ref(null); // 'inventory' or 'storage'
    const defaultSortOrder = [
      'หมวก', 'แว่นตา', 'เสื้อ', 'เสื้อคลุม', 'เกราะ', 'ปลอกแขน', 'ถุงมือ', 'กางเกง', 'สนับแข้ง', 'รองเท้า',
      'เข็มขัด', 'สร้อยคอ', 'แหวน', 'อาวุธ', 'อาวุธสำรอง', 'ตะกรุด', 'ผ้ายันต์', 'สายสิญจน์', 'หนังสือ', 'อาหาร', 'แผนที่', 'รักษา', 'กัปดัก', 'วัสดุ', 'ทั่วไป',
    ];
    const sortOrder = ref([...defaultSortOrder]);
    const typeOptions = [...defaultSortOrder];
    // Logs
    const logs = ref([]);
    const filterAction = ref('');
    const searchQuery = ref('');
    const logsLoading = ref(false);
    const logsError = ref(null);
    const lastVisibleLog = ref(null);
    const hasMoreLogs = ref(true);
    const logsPerPage = 10;
    // Computed Properties for Status
    const playerMaxHp = computed(() => {
      if (!characterPrivateData.value) return 0;
      const stamina =
        (characterPrivateData.value.itemStats?.stamina || 0) +
        (characterPrivateData.value.stats?.stamina || 0) +
        (characterPrivateData.value.traitStats?.stamina || 0) +
        (characterPrivateData.value.skillStats?.stamina || 0) +
        (characterPrivateData.value.titleStats?.stamina || 0) +
        (characterPrivateData.value.tattooStats?.stamina || 0) +
        (characterPrivateData.value.ghostStats?.stamina || 0) +
        (characterPrivateData.value.bonusStats?.stamina || 0);
      return stamina * 20 + (characterPrivateData.value.itemStats?.maxHp || 0) + (characterPrivateData.value.ghostStats?.maxHp || 0) + (characterPrivateData.value.bonusStats?.maxHp || 0);
    });
    const playerHp = computed(() => {
      if (!characterPrivateData.value) return 0;
      return Math.max(0, playerMaxHp.value - (characterPrivateData.value.damageTaken || 0));
    });
    const hpPercentage = computed(() =>
      playerMaxHp.value > 0 ? Math.min((playerHp.value / playerMaxHp.value) * 100, 100) : 0
    );
    const energyPercentage = computed(() =>
      Math.min(((characterPrivateData.value?.energyLevel || 0) / 4800) * 100, 100)
    );
    const foodPercentage = computed(() =>
      Math.min((characterPrivateData.value?.foodLevel || 0), 100)
    );
    const happinessPercentage = computed(() =>
      Math.min((characterPrivateData.value?.happyLevel || 0), 100)
    );
    const infectionPercentage = computed(() =>
      Math.min((characterPrivateData.value?.infectionLevel || 0), 100)
    );
    // Computed Properties for Stats
    const totalStats = computed(() => {
      if (!characterPrivateData.value) {
        return { str: 0, stamina: 0, int: 0, agi: 0, luck: 0, maxHp: 0, atk: 0, evasion: 0, critical: 0, defense: 0 };
      }
      const stats = characterPrivateData.value;
      return {
        str: (stats.itemStats?.str || 0) + (stats.stats?.str || 0) + (stats.traitStats?.str || 0) + (stats.skillStats?.str || 0) + (stats.titleStats?.str || 0) + (stats.tattooStats?.str || 0) + (stats.ghostStats?.str || 0) + (stats.bonusStats?.str || 0),
        stamina: (stats.itemStats?.stamina || 0) + (stats.stats?.stamina || 0) + (stats.traitStats?.stamina || 0) + (stats.skillStats?.stamina || 0) + (stats.titleStats?.stamina || 0) + (stats.tattooStats?.stamina || 0) + (stats.ghostStats?.stamina || 0) + (stats.bonusStats?.stamina || 0),
        int: (stats.itemStats?.int || 0) + (stats.stats?.int || 0) + (stats.traitStats?.int || 0) + (stats.skillStats?.int || 0) + (stats.titleStats?.int || 0) + (stats.tattooStats?.int || 0) + (stats.ghostStats?.int || 0) + (stats.bonusStats?.int || 0),
        agi: (stats.itemStats?.agi || 0) + (stats.stats?.agi || 0) + (stats.traitStats?.agi || 0) + (stats.skillStats?.agi || 0) + (stats.titleStats?.agi || 0) + (stats.tattooStats?.agi || 0) + (stats.ghostStats?.agi || 0) + (stats.bonusStats?.agi || 0),
        luck: (stats.itemStats?.luck || 0) + (stats.stats?.luck || 0) + (stats.traitStats?.luck || 0) + (stats.skillStats?.luck || 0) + (stats.titleStats?.luck || 0) + (stats.tattooStats?.luck || 0) + (stats.ghostStats?.luck || 0) + (stats.bonusStats?.luck || 0),
        maxHp: (stats.itemStats?.maxHp || 0) + (stats.stats?.maxHp || 0) + (stats.traitStats?.maxHp || 0) + (stats.skillStats?.maxHp || 0) + (stats.titleStats?.maxHp || 0) + (stats.tattooStats?.maxHp || 0) + (stats.ghostStats?.maxHp || 0) + (stats.bonusStats?.maxHp || 0),
        atk: (stats.itemStats?.atk || 0) + (stats.stats?.atk || 0) + (stats.traitStats?.atk || 0) + (stats.skillStats?.atk || 0) + (stats.titleStats?.atk || 0) + (stats.tattooStats?.atk || 0) + (stats.ghostStats?.atk || 0) + (stats.bonusStats?.atk || 0),
        evasion: (stats.itemStats?.evasion || 0) + (stats.stats?.evasion || 0) + (stats.traitStats?.evasion || 0) + (stats.skillStats?.evasion || 0) + (stats.titleStats?.evasion || 0) + (stats.tattooStats?.evasion || 0) + (stats.ghostStats?.evasion || 0) + (stats.bonusStats?.evasion || 0),
        critical: (stats.itemStats?.critical || 0) + (stats.stats?.critical || 0) + (stats.traitStats?.critical || 0) + (stats.skillStats?.critical || 0) + (stats.titleStats?.critical || 0) + (stats.tattooStats?.critical || 0) + (stats.ghostStats?.critical || 0) + (stats.bonusStats?.critical || 0),
        defense: (stats.itemStats?.defense || 0) + (stats.stats?.defense || 0) + (stats.traitStats?.defense || 0) + (stats.skillStats?.defense || 0) + (stats.titleStats?.defense || 0) + (stats.tattooStats?.defense || 0) + (stats.ghostStats?.defense || 0) + (stats.bonusStats?.defense || 0),
      };
    });
    const calculatedStats = computed(() => {
      const stats = totalStats.value;
      return {
        atk: stats.str * 2 + stats.atk,
        critical: Math.floor((stats.int / 2) + stats.critical),
        maxHp: stats.stamina * 20 + stats.maxHp,
        evasion: Math.floor((stats.agi / 2) + stats.evasion),
        defense: stats.defense,
        criticalReduction: Math.floor(stats.luck) + Math.floor(stats.defense / 20),
        dropRateBonus: stats.luck * 0.01,
        damageReduction: Math.floor(stats.defense / 20),
      };
    });
    const calculatedNormalItemStats = computed(() => {
      const stats = {
        stamina: 0, str: 0, int: 0, agi: 0, luck: 0, maxHp: 0, atk: 0, evasion: 0, critical: 0, defense: 0,
      };
      equippedItems.value.forEach((item) => {
        const itemStats = item.stats || {};
        const upgradeStats = item.upgradeStats || {};
        const cardStats = item.cardStats || {};
        stats.stamina += (itemStats.stamina || 0) + (upgradeStats.stamina || 0) + (cardStats.stamina || 0);
        stats.str += (itemStats.str || 0) + (upgradeStats.str || 0) + (cardStats.str || 0);
        stats.int += (itemStats.int || 0) + (upgradeStats.int || 0) + (cardStats.int || 0);
        stats.agi += (itemStats.agi || 0) + (upgradeStats.agi || 0) + (cardStats.agi || 0);
        stats.luck += (itemStats.luck || 0) + (upgradeStats.luck || 0) + (cardStats.luck || 0);
        stats.maxHp += (itemStats.maxHp || 0) + (upgradeStats.maxHp || 0) + (cardStats.maxHp || 0);
        stats.atk += (itemStats.atk || 0) + (upgradeStats.atk || 0) + (cardStats.atk || 0);
        stats.evasion += (itemStats.evasion || 0) + (upgradeStats.evasion || 0) + (cardStats.evasion || 0);
        stats.critical += (itemStats.critical || 0) + (upgradeStats.critical || 0) + (cardStats.critical || 0);
        stats.defense += (itemStats.defense || 0) + (upgradeStats.defense || 0) + (cardStats.defense || 0);
      });
      return stats;
    });
    const calculatedMechanicalItemStats = computed(() => {
      const stats = {
        stamina: 0, str: 0, int: 0, agi: 0, luck: 0, maxHp: 0, atk: 0, evasion: 0, critical: 0, defense: 0,
      };
      equippedMechanicalOrgans.value.forEach((item) => {
        const itemStats = item.stats || {};
        const upgradeStats = item.upgradeStats || {};
        const cardStats = item.cardStats || {};
        stats.stamina += (itemStats.stamina || 0) + (upgradeStats.stamina || 0) + (cardStats.stamina || 0);
        stats.str += (itemStats.str || 0) + (upgradeStats.str || 0) + (cardStats.str || 0);
        stats.int += (itemStats.int || 0) + (upgradeStats.int || 0) + (cardStats.int || 0);
        stats.agi += (itemStats.agi || 0) + (upgradeStats.agi || 0) + (cardStats.agi || 0);
        stats.luck += (itemStats.luck || 0) + (upgradeStats.luck || 0) + (cardStats.luck || 0);
        stats.maxHp += (itemStats.maxHp || 0) + (upgradeStats.maxHp || 0) + (cardStats.maxHp || 0);
        stats.atk += (itemStats.atk || 0) + (upgradeStats.atk || 0) + (cardStats.atk || 0);
        stats.evasion += (itemStats.evasion || 0) + (upgradeStats.evasion || 0) + (cardStats.evasion || 0);
        stats.critical += (itemStats.critical || 0) + (upgradeStats.critical || 0) + (cardStats.critical || 0);
        stats.defense += (itemStats.defense || 0) + (upgradeStats.defense || 0) + (cardStats.defense || 0);
      });
      return stats;
    });
    const calculatedItemStats = computed(() => {
      const stats = {
        stamina: calculatedNormalItemStats.value.stamina + calculatedMechanicalItemStats.value.stamina,
        str: calculatedNormalItemStats.value.str + calculatedMechanicalItemStats.value.str,
        int: calculatedNormalItemStats.value.int + calculatedMechanicalItemStats.value.int,
        agi: calculatedNormalItemStats.value.agi + calculatedMechanicalItemStats.value.agi,
        luck: calculatedNormalItemStats.value.luck + calculatedMechanicalItemStats.value.luck,
        maxHp: calculatedNormalItemStats.value.maxHp + calculatedMechanicalItemStats.value.maxHp,
        atk: calculatedNormalItemStats.value.atk + calculatedMechanicalItemStats.value.atk,
        evasion: calculatedNormalItemStats.value.evasion + calculatedMechanicalItemStats.value.evasion,
        critical: calculatedNormalItemStats.value.critical + calculatedMechanicalItemStats.value.critical,
        defense: calculatedNormalItemStats.value.defense + calculatedMechanicalItemStats.value.defense,
      };
      return stats;
    });
    const calculatedGhostStats = computed(() => {
      const stats = {
        stamina: 0, str: 0, int: 0, agi: 0, luck: 0, maxHp: 0, atk: 0, evasion: 0, critical: 0, defense: 0,
      };
      ghostItems.value.forEach((item) => {
        const itemStats = item.bonusStats || item.stats || {};
        stats.stamina += itemStats.stamina || 0;
        stats.str += itemStats.str || 0;
        stats.int += itemStats.int || 0;
        stats.agi += itemStats.agi || 0;
        stats.luck += itemStats.luck || 0;
        stats.maxHp += itemStats.maxHp || 0;
        stats.atk += itemStats.atk || 0;
        stats.evasion += itemStats.evasion || 0;
        stats.critical += itemStats.critical || 0;
        stats.defense += itemStats.defense || 0;
      });
      return stats;
    });
    const calculatedTattooStats = computed(() => {
      const stats = {
        stamina: 0, str: 0, int: 0, agi: 0, luck: 0,
      };
      tattoos.value.forEach((tattoo) => {
        if (tattoo.stats) {
          stats.stamina += tattoo.stats.stamina || 0;
          stats.str += tattoo.stats.str || 0;
          stats.int += tattoo.stats.int || 0;
          stats.agi += tattoo.stats.agi || 0;
          stats.luck += tattoo.stats.luck || 0;
        }
      });
      return stats;
    });
    const calculatedSkillStats = computed(() => {
      const stats = {
        stamina: 0, str: 0, int: 0, agi: 0, luck: 0,
      };
      skills.value.forEach((skill) => {
        if (skill.stats) {
          stats.stamina += skill.stats.stamina || 0;
          stats.str += skill.stats.str || 0;
          stats.int += skill.stats.int || 0;
          stats.agi += skill.stats.agi || 0;
          stats.luck += skill.stats.luck || 0;
        }
      });
      return stats;
    });
    const calculatedTitleStats = computed(() => {
      const stats = {
        stamina: 0, str: 0, int: 0, agi: 0, luck: 0
      };
      if (currentTitle.value && titles[currentTitle.value]) {
        const titleStats = titles[currentTitle.value].stats || {};
        stats.stamina = titleStats.stamina || 0;
        stats.str = titleStats.str || 0;
        stats.int = titleStats.int || 0;
        stats.agi = titleStats.agi || 0;
        stats.luck = titleStats.luck || 0;
      }
      return stats;
    });
    const calculatedBonusStats = computed(() => {
      const stats = {
        stamina: 0, str: 0, int: 0, agi: 0, luck: 0, maxHp: 0, atk: 0, evasion: 0, critical: 0, defense: 0
      };
      for (const [setName, config] of Object.entries(setConfigs)) {
        const equippedNames = equippedItems.value.map(item => item.name).filter(Boolean);
        const equippedInSet = equippedNames.filter(name => config.items.includes(name)).length;
        if (equippedInSet === 0) continue;
        const bonusKeys = Object.keys(config.bonuses).map(Number).sort((a, b) => b - a);
        for (const required of bonusKeys) {
          if (equippedInSet >= required) {
            const bonus = config.bonuses[required];
            for (const [stat, value] of Object.entries(bonus)) {
              stats[stat] = (stats[stat] || 0) + value;
            }
            break;
          }
        }
      }
      return stats;
    });
    const isItemStatsMatching = computed(() => {
      const itemStats = characterPrivateData.value.itemStats || {};
      return (
        (itemStats.stamina || 0) === calculatedItemStats.value.stamina &&
        (itemStats.str || 0) === calculatedItemStats.value.str &&
        (itemStats.int || 0) === calculatedItemStats.value.int &&
        (itemStats.agi || 0) === calculatedItemStats.value.agi &&
        (itemStats.luck || 0) === calculatedItemStats.value.luck &&
        (itemStats.maxHp || 0) === calculatedItemStats.value.maxHp &&
        (itemStats.atk || 0) === calculatedItemStats.value.atk &&
        (itemStats.evasion || 0) === calculatedItemStats.value.evasion &&
        (itemStats.critical || 0) === calculatedItemStats.value.critical &&
        (itemStats.defense || 0) === calculatedItemStats.value.defense
      );
    });
    const isGhostStatsMatching = computed(() => {
      const ghostStats = characterPrivateData.value.ghostStats || {};
      return (
        (ghostStats.stamina || 0) === calculatedGhostStats.value.stamina &&
        (ghostStats.str || 0) === calculatedGhostStats.value.str &&
        (ghostStats.int || 0) === calculatedGhostStats.value.int &&
        (ghostStats.agi || 0) === calculatedGhostStats.value.agi &&
        (ghostStats.luck || 0) === calculatedGhostStats.value.luck &&
        (ghostStats.maxHp || 0) === calculatedGhostStats.value.maxHp &&
        (ghostStats.atk || 0) === calculatedGhostStats.value.atk &&
        (ghostStats.evasion || 0) === calculatedGhostStats.value.evasion &&
        (ghostStats.critical || 0) === calculatedGhostStats.value.critical &&
        (ghostStats.defense || 0) === calculatedGhostStats.value.defense
      );
    });
    const isTattooStatsMatching = computed(() => {
      const tattooStats = characterPrivateData.value.tattooStats || {};
      return (
        (tattooStats.stamina || 0) === calculatedTattooStats.value.stamina &&
        (tattooStats.str || 0) === calculatedTattooStats.value.str &&
        (tattooStats.int || 0) === calculatedTattooStats.value.int &&
        (tattooStats.agi || 0) === calculatedTattooStats.value.agi &&
        (tattooStats.luck || 0) === calculatedTattooStats.value.luck
      );
    });
    const isSkillStatsMatching = computed(() => {
      const skillStats = characterPrivateData.value.skillStats || {};
      return (
        (skillStats.stamina || 0) === calculatedSkillStats.value.stamina &&
        (skillStats.str || 0) === calculatedSkillStats.value.str &&
        (skillStats.int || 0) === calculatedSkillStats.value.int &&
        (skillStats.agi || 0) === calculatedSkillStats.value.agi &&
        (skillStats.luck || 0) === calculatedSkillStats.value.luck
      );
    });
    const isTitleStatsMatching = computed(() => {
      const titleStats = characterPrivateData.value.titleStats || {};
      return (
        (titleStats.stamina || 0) === calculatedTitleStats.value.stamina &&
        (titleStats.str || 0) === calculatedTitleStats.value.str &&
        (titleStats.int || 0) === calculatedTitleStats.value.int &&
        (titleStats.agi || 0) === calculatedTitleStats.value.agi &&
        (titleStats.luck || 0) === calculatedTitleStats.value.luck
      );
    });
    const isBonusStatsMatching = computed(() => {
      const bonusStats = characterPrivateData.value.bonusStats || {};
      return (
        (bonusStats.stamina || 0) === calculatedBonusStats.value.stamina &&
        (bonusStats.str || 0) === calculatedBonusStats.value.str &&
        (bonusStats.int || 0) === calculatedBonusStats.value.int &&
        (bonusStats.agi || 0) === calculatedBonusStats.value.agi &&
        (bonusStats.luck || 0) === calculatedBonusStats.value.luck &&
        (bonusStats.maxHp || 0) === calculatedBonusStats.value.maxHp &&
        (bonusStats.atk || 0) === calculatedBonusStats.value.atk &&
        (bonusStats.evasion || 0) === calculatedBonusStats.value.evasion &&
        (bonusStats.critical || 0) === calculatedBonusStats.value.critical &&
        (bonusStats.defense || 0) === calculatedBonusStats.value.defense
      );
    });
    const calculatedBagWeight = computed(() => {
      return inventoryItems.value.reduce((sum, item) => sum + (item.weight || 0) * (item.quantity || 1), 0);
    });
    const calculatedStorageWeight = computed(() => {
      return storageItems.value.reduce((sum, item) => sum + (item.weight || 0) * (item.quantity || 1), 0);
    });
    const isBagWeightMatching = computed(() => {
      return currentWeight.value === calculatedBagWeight.value;
    });
    const isStorageWeightMatching = computed(() => {
      return storageWeight.value === calculatedStorageWeight.value;
    });
    const nextLevelExp = computed(() => {
      if (!characterData.value) return 0;
      const currentLevel = characterData.value.level || 1;
      const levelData = LEVEL_EXP_TABLE.find(data => data.level === currentLevel);
      return levelData ? levelData.expRequired : 0;
    });
    const experiencePercentage = computed(() => {
      if (!characterPrivateData.value || !nextLevelExp.value) return 0;
      const currentExp = characterPrivateData.value.experience || 0;
      return Math.min(100, (currentExp / nextLevelExp.value) * 100);
    });
    // Computed Properties for Inventory and Storage
    const filteredInventoryItems = computed(() => {
      let items = [...inventoryItems.value];
      const typeOrder = sortOrder.value.reduce((acc, type, index) => {
        acc[type] = index + 1;
        return acc;
      }, {});
      items.sort((a, b) => {
        const orderA = typeOrder[a.type] || 999;
        const orderB = typeOrder[b.type] || 999;
        if (orderA !== orderB) return orderA - orderB;
        const rarityA = a.rarity ? String(a.rarity) : '';
        const rarityB = b.rarity ? String(b.rarity) : '';
        if (rarityA !== rarityB) return rarityA.localeCompare(rarityB);
        return a.name.localeCompare(b.name, 'th');
      });
      return items.filter(item => {
        const matchesType = inventorySelectedType.value ? item.type === inventorySelectedType.value : true;
        const matchesSearch = inventorySearchQuery.value
          ? item.name.toLowerCase().includes(inventorySearchQuery.value.toLowerCase())
          : true;
        return matchesType && matchesSearch;
      });
    });
    const filteredStorageItems = computed(() => {
      let items = [...storageItems.value];
      const typeOrder = sortOrder.value.reduce((acc, type, index) => {
        acc[type] = index + 1;
        return acc;
      }, {});
      items.sort((a, b) => {
        const orderA = typeOrder[a.type] || 999;
        const orderB = typeOrder[b.type] || 999;
        if (orderA !== orderB) return orderA - orderB;
        const rarityA = a.rarity ? String(a.rarity) : '';
        const rarityB = b.rarity ? String(b.rarity) : '';
        if (rarityA !== rarityB) return rarityA.localeCompare(rarityB);
        return a.name.localeCompare(b.name, 'th');
      });
      return items.filter(item => {
        const matchesType = storageSelectedType.value ? item.type === storageSelectedType.value : true;
        const matchesSearch = storageSearchQuery.value
          ? item.name.toLowerCase().includes(storageSearchQuery.value.toLowerCase())
          : true;
        return matchesType && matchesSearch;
      });
    });
    // Handle image load errors
    const handleImageError = event => {
      event.target.src = 'default-item.png';
    };
    // Select an item
    const selectItem = item => {
      selectedItem.value = item;
    };
    // Open detail modal on double click
    const openDetailModal = item => {
      selectedItem.value = item;
      showDetailModal.value = true;
    };
    // Navigation
    const goBack = () => {
      router.push('/admin');
    };
    const showStatDetails = stat => {
      selectedStat.value = stat;
      toggleModal.value = 'StatDetailsModal';
    };
    const statBreakdown = computed(() => {
      if (!characterPrivateData.value || !selectedStat.value) return {};
      const stats = characterPrivateData.value;
      const stat = selectedStat.value.toLowerCase();
      return {
        item: stats.itemStats?.[stat] || 0,
        tattoo: stats.tattooStats?.[stat] || 0,
        base: stats.stats?.[stat] || 0,
        trait: stats.traitStats?.[stat] || 0,
        skill: stats.skillStats?.[stat] || 0,
        title: stats.titleStats?.[stat] || 0,
        ghost: stats.ghostStats?.[stat] || 0,
        bonus: stats.bonusStats?.[stat] || 0,
        total: totalStats.value[stat]
      };
    });
    const showPowerDetails = power => {
      selectedPower.value = power;
      toggleModal.value = 'PowerDetailsModal';
    };
    const powerBreakdown = computed(() => {
      if (!characterPrivateData.value || !selectedPower.value) return {};
      const stats = characterPrivateData.value;
      const power = selectedPower.value;
      const sources = {
        base: stats.stats || {},
        item: stats.itemStats || {},
        trait: stats.traitStats || {},
        skill: stats.skillStats || {},
        title: stats.titleStats || {},
        tattoo: stats.tattooStats || {},
        ghost: stats.ghostStats || {},
        bonus: stats.bonusStats || {}
      };
      const breakdown = {
        base: 0,
        item: 0,
        trait: 0,
        skill: 0,
        title: 0,
        tattoo: 0,
        ghost: 0,
        bonus: 0,
        total: 0,
      };
      if (power === 'maxHp') {
        const multiplier = 20;
        breakdown.base = (sources.base.stamina || 0) * multiplier + (sources.base.maxHp || 0);
        breakdown.item = (sources.item.stamina || 0) * multiplier + (sources.item.maxHp || 0);
        breakdown.trait = (sources.trait.stamina || 0) * multiplier + (sources.trait.maxHp || 0);
        breakdown.skill = (sources.skill.stamina || 0) * multiplier + (sources.skill.maxHp || 0);
        breakdown.title = (sources.title.stamina || 0) * multiplier + (sources.title.maxHp || 0);
        breakdown.tattoo = (sources.tattoo.stamina || 0) * multiplier + (sources.tattoo.maxHp || 0);
        breakdown.ghost = (sources.ghost.stamina || 0) * multiplier + (sources.ghost.maxHp || 0);
        breakdown.bonus = (sources.bonus.stamina || 0) * multiplier + (sources.bonus.maxHp || 0);
      } else if (power === 'atk') {
        const multiplier = 2;
        breakdown.base = (sources.base.str || 0) * multiplier + (sources.base.atk || 0);
        breakdown.item = (sources.item.str || 0) * multiplier + (sources.item.atk || 0);
        breakdown.trait = (sources.trait.str || 0) * multiplier + (sources.trait.atk || 0);
        breakdown.skill = (sources.skill.str || 0) * multiplier + (sources.skill.atk || 0);
        breakdown.title = (sources.title.str || 0) * multiplier + (sources.title.atk || 0);
        breakdown.tattoo = (sources.tattoo.str || 0) * multiplier + (sources.tattoo.atk || 0);
        breakdown.ghost = (sources.ghost.str || 0) * multiplier + (sources.ghost.atk || 0);
        breakdown.bonus = (sources.bonus.str || 0) * multiplier + (sources.bonus.atk || 0);
      } else if (power === 'critical') {
        breakdown.base = Math.floor((sources.base.int || 0) / 2) + (sources.base.critical || 0);
        breakdown.item = Math.floor((sources.item.int || 0) / 2) + (sources.item.critical || 0);
        breakdown.trait = Math.floor((sources.trait.int || 0) / 2) + (sources.trait.critical || 0);
        breakdown.skill = Math.floor((sources.skill.int || 0) / 2) + (sources.skill.critical || 0);
        breakdown.title = Math.floor((sources.title.int || 0) / 2) + (sources.title.critical || 0);
        breakdown.tattoo = Math.floor((sources.tattoo.int || 0) / 2) + (sources.tattoo.critical || 0);
        breakdown.ghost = Math.floor((sources.ghost.int || 0) / 2) + (sources.ghost.critical || 0);
        breakdown.bonus = Math.floor((sources.bonus.int || 0) / 2) + (sources.bonus.critical || 0);
      } else if (power === 'evasion') {
        breakdown.base = Math.floor((sources.base.agi || 0) / 2) + (sources.base.evasion || 0);
        breakdown.item = Math.floor((sources.item.agi || 0) / 2) + (sources.item.evasion || 0);
        breakdown.trait = Math.floor((sources.trait.agi || 0) / 2) + (sources.trait.evasion || 0);
        breakdown.skill = Math.floor((sources.skill.agi || 0) / 2) + (sources.skill.evasion || 0);
        breakdown.title = Math.floor((sources.title.agi || 0) / 2) + (sources.title.evasion || 0);
        breakdown.tattoo = Math.floor((sources.tattoo.agi || 0) / 2) + (sources.tattoo.evasion || 0);
        breakdown.ghost = Math.floor((sources.ghost.agi || 0) / 2) + (sources.ghost.evasion || 0);
        breakdown.bonus = Math.floor((sources.bonus.agi || 0) / 2) + (sources.bonus.evasion || 0);
      } else if (power === 'defense') {
        breakdown.base = sources.base.defense || 0;
        breakdown.item = sources.item.defense || 0;
        breakdown.trait = sources.trait.defense || 0;
        breakdown.skill = sources.skill.defense || 0;
        breakdown.title = sources.title.defense || 0;
        breakdown.tattoo = sources.tattoo.defense || 0;
        breakdown.ghost = sources.ghost.defense || 0;
        breakdown.bonus = sources.bonus.defense || 0;
      } else if (power === 'criticalReduction') {
        breakdown.base = Math.floor(sources.base.luck || 0) + Math.floor((sources.base.defense || 0) / 20);
        breakdown.item = Math.floor(sources.item.luck || 0) + Math.floor((sources.item.defense || 0) / 20);
        breakdown.trait = Math.floor(sources.trait.luck || 0) + Math.floor((sources.trait.defense || 0) / 20);
        breakdown.skill = Math.floor(sources.skill.luck || 0) + Math.floor((sources.skill.defense || 0) / 20);
        breakdown.title = Math.floor(sources.title.luck || 0) + Math.floor((sources.title.defense || 0) / 20);
        breakdown.tattoo = Math.floor(sources.tattoo.luck || 0) + Math.floor((sources.tattoo.defense || 0) / 20);
        breakdown.ghost = Math.floor(sources.ghost.luck || 0) + Math.floor((sources.ghost.defense || 0) / 20);
        breakdown.bonus = Math.floor(sources.bonus.luck || 0) + Math.floor((sources.bonus.defense || 0) / 20);
      } else if (power === 'dropRateBonus') {
        breakdown.base = (sources.base.luck || 0) * 0.01;
        breakdown.item = (sources.item.luck || 0) * 0.01;
        breakdown.trait = (sources.trait.luck || 0) * 0.01;
        breakdown.skill = (sources.skill.luck || 0) * 0.01;
        breakdown.title = (sources.title.luck || 0) * 0.01;
        breakdown.tattoo = (sources.tattoo.luck || 0) * 0.01;
        breakdown.ghost = (sources.ghost.luck || 0) * 0.01;
        breakdown.bonus = (sources.bonus.luck || 0) * 0.01;
      } else if (power === 'damageReduction') {
        breakdown.base = Math.floor((sources.base.defense || 0) / 20);
        breakdown.item = Math.floor((sources.item.defense || 0) / 20);
        breakdown.trait = Math.floor((sources.trait.defense || 0) / 20);
        breakdown.skill = Math.floor((sources.skill.defense || 0) / 20);
        breakdown.title = Math.floor((sources.title.defense || 0) / 20);
        breakdown.tattoo = Math.floor((sources.tattoo.defense || 0) / 20);
        breakdown.ghost = Math.floor((sources.ghost.defense || 0) / 20);
        breakdown.bonus = Math.floor((sources.bonus.defense || 0) / 20);
      }
      return breakdown;
    });
    // Sorting Functions
    const loadSortOrder = () => {
      try {
        const savedOrder = localStorage.getItem('sortOrder');
        if (savedOrder) {
          const parsedOrder = JSON.parse(savedOrder);
          if (parsedOrder.length === defaultSortOrder.length && defaultSortOrder.every(type => parsedOrder.includes(type))) {
            sortOrder.value = parsedOrder;
          }
        }
      } catch (err) {
        console.error('Error loading sort order from localStorage:', err);
        error.value = 'ไม่สามารถโหลดลำดับการจัดเรียง: ' + err.message;
      }
    };
    const saveSortOrderToLocal = () => {
      try {
        localStorage.setItem('sortOrder', JSON.stringify(sortOrder.value));
      } catch (err) {
        console.error('Error saving sort order to localStorage:', err);
        error.value = 'ไม่สามารถบันทึกลำดับการจัดเรียง: ' + err.message;
      }
    };
    const resetSortOrder = () => {
      sortOrder.value = [...defaultSortOrder];
      saveSortOrderToLocal();
    };
    const openSortModal = context => {
      sortContext.value = context;
      showSortModal.value = true;
    };
    const moveUp = index => {
      if (index === 0) return;
      const newOrder = [...sortOrder.value];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
      sortOrder.value = newOrder;
    };
    const moveDown = index => {
      if (index === sortOrder.value.length - 1) return;
      const newOrder = [...sortOrder.value];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      sortOrder.value = newOrder;
    };
    const saveSortOrder = async () => {
      isSorting.value = true;
      try {
        saveSortOrderToLocal();
        showSortModal.value = false;
      } catch (err) {
        error.value = err.message || 'เกิดข้อผิดพลาดในการบันทึกการจัดเรียง';
      } finally {
        isSorting.value = false;
      }
    };
    // Logs Functions
    const getActionName = action => {
      const actions = {
        explore: 'ผจญภัย',
        new_user_signup: 'สมัครสมาชิก',
        create_character: 'สร้างตัวละคร',
        upgrade_stats: 'อัปเกรดสเตตัส',
        attack_zombie: 'โจมตีซอมบี้',
        flee_fight: 'หนีการต่อสู้',
        claim_rewards: 'รับรางวัล',
        go_home: 'กลับบ้าน',
        add_to_bag: 'ใส่ไอเทมลงกระเป๋า',
        discard_item: 'ทิ้งไอเทม',
        equip_item: 'ใส่ไอเทม',
        unequip_item: 'ถอนไอเทม',
        move_item_to_storage: 'ย้ายไอเทมไปคลัง',
        move_item_to_bag: 'ย้ายไอเทมไปกระเป๋า',
        change_trait: 'เปลี่ยนลักษณะนิสัย',
        learn_skill: 'เรียนรู้ทักษะ',
        tattoo_skin: 'สักลาย',
        unlock_title: 'ปลดล็อกฉายา',
        use_title: 'ใช้งานฉายา',
        heal_health: 'รักษาเลือด',
        heal_infection: 'รักษาการติดเชื้อ',
        sell_item: 'ขายไอเทม',
        buy_item: 'ซื้อไอเทม',
        buy_food: 'ซื้ออาหาร',
        unlock_room: 'ปลดล็อกห้อง',
        assassinate_criminal: 'ลอบสังหารอาชญากร',
        punish: 'ลงโทษ',
        reborn: 'เกิดใหม่',
        upgrade_storage: 'อัปเกรดคลัง',
        upgrade_bag: 'อัปเกรดกระเป๋า',
        move_room: 'ย้ายห้อง',
        pvp: 'การต่อสู้ PvP',
        depletionDeath: 'เสียชีวิต',
        sell_items: 'ขายไอเทม',
        energyIncrease: 'เพิ่มพลังงาน',
        claim_mysterious_item: 'สุ่มรับไอเทมคลังปริศนา',
        claim_mysterious_item_with_diamonds: 'สุ่มรับไอเทมกล่องปริศนาด้วยแคช',
        claim_mysterious_item_free: 'สุ่มรับไอเทมกล่องปริศนาฟรี',
        consume_food: 'กินอาหาร',
        healReset: 'รีเซ็ตฮีล',
      };
      return actions[action] || action;
    };
    const formatTimestamp = timestamp => {
      if (!timestamp) return 'ไม่ระบุ';
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString('th-TH', {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    };
    const checkWarnings = log => {
      if (log.details?.infectionLevel >= 80 || log.details?.previousInfectionLevel >= 80) {
        return { type: 'warning', message: 'ระดับการติดเชื้อสูง! รักษาโดยด่วน' };
      }
      if (log.details?.newFoodLevel <= 20) {
        return { type: 'warning', message: 'ระดับอาหารต่ำ! ซื้ออาหารเพิ่มเติม' };
      }
      if (log.details?.newHappyLevel <= 20) {
        return { type: 'warning', message: 'ระดับความสุขต่ำ! ซื้ออาหารที่มีความสุขสูงขึ้น' };
      }
      if (log.details?.newEnergy <= 20 || log.details?.newEnergyLevel <= 20) {
        return { type: 'warning', message: 'พลังงานต่ำ! พักผ่อนหรือใช้ไอเทมเพิ่มพลังงาน' };
      }
      if (log.details?.newCashBalance <= 20) {
        return { type: 'warning', message: 'แคชเหลือน้อย! หาแคชเพิ่มเติม' };
      }
      return null;
    };
    const filteredLogs = computed(() => {
      return logs.value.filter(log => {
        const matchesAction = filterAction.value ? log.action === filterAction.value : true;
        const searchLower = searchQuery.value.toLowerCase();
        const matchesSearch = !searchQuery.value || log.displayMessage.toLowerCase().includes(searchLower);
        return matchesAction && matchesSearch;
      });
    });
    const applyFilters = () => {
      fetchLogs();
    };
    // Fetch Logs
    const fetchLogs = async () => {
      if (!userId.value) {
        logsError.value = 'ไม่พบ ID ผู้ใช้';
        logsLoading.value = false;
        return;
      }
      logsLoading.value = true;
      try {
        const query = projectFirestore
          .collection('logs')
          .where('uid', '==', userId.value)
          .orderBy('timestamp', 'desc')
          .limit(logsPerPage);
        const snapshot = await query.get();
        logs.value = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        lastVisibleLog.value = snapshot.docs[snapshot.docs.length - 1];
        hasMoreLogs.value = snapshot.docs.length === logsPerPage;
        logsLoading.value = false;
        updateLoadingState();
      } catch (err) {
        logsError.value = err.message || 'เกิดข้อผิดพลาดในการโหลดประวัติ';
        logsLoading.value = false;
        updateLoadingState();
      }
    };
    // Load More Logs
    const loadMoreLogs = async () => {
      if (!lastVisibleLog.value || logsLoading.value) return;
      logsLoading.value = true;
      const currentLogCount = logs.value.length;
      try {
        const query = projectFirestore
          .collection('logs')
          .where('uid', '==', userId.value)
          .orderBy('timestamp', 'desc')
          .startAfter(lastVisibleLog.value)
          .limit(logsPerPage);
        const snapshot = await query.get();
        const newLogs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        logs.value = [...logs.value, ...newLogs];
        lastVisibleLog.value = snapshot.docs[snapshot.docs.length - 1];
        hasMoreLogs.value = snapshot.docs.length === logsPerPage;
        logsLoading.value = false;
        updateLoadingState();
        if (newLogs.length > 0) {
          setTimeout(() => {
            const newLogElement = document.getElementById(`log-${logs.value[currentLogCount].id}`);
            if (newLogElement) {
              newLogElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
        }
      } catch (err) {
        logsError.value = err.message || 'เกิดข้อผิดพลาดในการโหลดประวัติเพิ่มเติม';
        logsLoading.value = false;
        updateLoadingState();
      }
    };
    // Handle Scroll Event
    const handleScroll = () => {
      const logsContainer = document.querySelector('.logs-list');
      if (!logsContainer || logsLoading.value || !hasMoreLogs.value) return;
      const { scrollTop, scrollHeight, clientHeight } = logsContainer;
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        loadMoreLogs();
      }
    };
    // Update loading state
    const updateLoadingState = () => {
      loading.value = equippedLoading.value || inventoryLoading.value || storageLoading.value || logsLoading.value ||
        !characterData.value || !characterPrivateData.value || !userData.value;
    };
    // Fetch User Data (for cash)
    const subscribeToUserData = () => {
      if (!userId.value) {
        error.value = 'ไม่พบ ID ผู้ใช้';
        loading.value = false;
        return () => {};
      }
      const unsubscribe = projectFirestore
        .collection('users')
        .doc(userId.value)
        .onSnapshot(
          doc => {
            if (doc.exists) {
              userData.value = doc.data();
            } else {
              userData.value = null;
              error.value = 'ไม่พบข้อมูลผู้ใช้';
            }
            updateLoadingState();
          },
          err => {
            error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลผู้ใช้: ' + err.message;
            loading.value = false;
          }
        );
      return unsubscribe;
    };
    // Fetch Character Data
    const subscribeToCharacterData = () => {
      if (!userId.value) {
        error.value = 'ไม่พบ ID ผู้ใช้';
        loading.value = false;
        return { unsubscribeCharacter: () => {}, unsubscribePrivate: () => {} };
      }
      const unsubscribeCharacter = projectFirestore
        .collection('characters')
        .doc(userId.value)
        .onSnapshot(
          doc => {
            if (doc.exists) {
              characterData.value = doc.data();
              currentTitle.value = doc.data().title || null;
            } else {
              characterData.value = null;
              currentTitle.value = null;
              error.value = 'ไม่พบข้อมูลตัวละคร';
            }
            updateLoadingState();
          },
          err => {
            error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลตัวละคร: ' + err.message;
            loading.value = false;
          }
        );
      const unsubscribePrivate = projectFirestore
        .collection('characterPrivate')
        .doc(userId.value)
        .onSnapshot(
          async doc => {
            if (doc.exists) {
              const charPrivateData = doc.data();
              characterPrivateData.value = {
                ...charPrivateData,
                itemStats: charPrivateData.itemStats || { str: 0, stamina: 0, int: 0, agi: 0, luck: 0, maxHp: 0, atk: 0, evasion: 0, critical: 0, defense: 0 },
                bonusStats: charPrivateData.bonusStats || { str: 0, stamina: 0, int: 0, agi: 0, luck: 0, maxHp: 0, atk: 0, evasion: 0, critical: 0, defense: 0 },
                stats: charPrivateData.stats || { str: 0, stamina: 0, int: 0, agi: 0, luck: 0 },
                traitStats: charPrivateData.traitStats || { str: 0, stamina: 0, int: 0, agi: 0, luck: 0 },
                skillStats: charPrivateData.skillStats || { str: 0, stamina: 0, int: 0, agi: 0, luck: 0 },
                titleStats: charPrivateData.titleStats || { str: 0, stamina: 0, int: 0, agi: 0, luck: 0 },
                tattooStats: charPrivateData.tattooStats || { str: 0, stamina: 0, int: 0, agi: 0, luck: 0 },
                ghostStats: charPrivateData.ghostStats || { str: 0, stamina: 0, int: 0, agi: 0, luck: 0, maxHp: 0, atk: 0, evasion: 0, critical: 0, defense: 0 },
                damageTaken: charPrivateData.damageTaken || 0,
                experience: charPrivateData.experience || 0,
                foodLevel: charPrivateData.foodLevel || 0,
                happyLevel: charPrivateData.happyLevel || 0,
                energyLevel: charPrivateData.energyLevel || 0,
                infectionLevel: charPrivateData.infectionLevel || 0,
                statPoints: charPrivateData.statPoints || 0,
              };
              itemNames.value = charPrivateData.itemNames || [];
              // Fetch tattoos
              const tattooNames = charPrivateData.tattooNames || [];
              if (tattooNames.length) {
                const tattooPromises = tattooNames.map(id => projectFirestore.collection('tattoos').doc(id).get());
                const tattooDocs = await Promise.all(tattooPromises);
                tattoos.value = tattooDocs.filter(d => d.exists).map(d => ({ id: d.id, ...d.data() }));
              } else {
                tattoos.value = [];
              }
              // Fetch skills
              const learnedSkills = charPrivateData.learnedSkills || [];
              if (learnedSkills.length) {
                skills.value = learnedSkills
                  .map(id => {
                    const data = gameItemsData[id];
                    return data ? { id, ...data } : null;
                  })
                  .filter(Boolean);
              } else {
                skills.value = [];
              }
              // V2: ghost items from ghostItems object map
              const ghostItemsData = charPrivateData.ghostItems || {};
              if (Object.keys(ghostItemsData).length > 0) {
                ghostItems.value = Object.entries(ghostItemsData).map(([instanceId, item]) => {
                  const tpl = gameItemsData[item.templateId] || {};
                  const states = item.states || {};
                  return {
                    id: instanceId,
                    instanceId,
                    name: tpl.name || item.templateId,
                    ...tpl,
                    ...states,
                  };
                });
              } else {
                ghostItems.value = [];
              }
            } else {
              characterPrivateData.value = null;
              tattoos.value = [];
              skills.value = [];
              ghostItems.value = [];
              itemNames.value = [];
              error.value = 'ไม่พบข้อมูลส่วนตัวของตัวละคร';
            }
            updateLoadingState();
          },
          err => {
            error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลส่วนตัว: ' + err.message;
            loading.value = false;
          }
        );
      return { unsubscribeCharacter, unsubscribePrivate };
    };
    // Fetch Equipped Items (including vehicle and mechanical organs)
    const subscribeToEquippedItems = () => {
      if (!userId.value) {
        equippedError.value = 'ไม่พบ ID ผู้ใช้';
        equippedLoading.value = false;
        return () => {};
      }
      const unsubscribe = projectFirestore
        .collection('characterPrivate')
        .doc(userId.value)
        .onSnapshot(
          async doc => {
            if (doc.exists) {
              const charPrivateData = doc.data();
              const vehicleId = charPrivateData?.vehicle || null;
              // V2: equippedItems + equippedItemIds
              const equippedItemsData = charPrivateData?.equippedItems || {};
              const equippedItemIds = charPrivateData?.equippedItemIds || {};
              equippedItemTypes.value = Object.keys(equippedItemIds);
              // V2: equipped items from equippedItems object map
              if (Object.keys(equippedItemsData).length > 0) {
                equippedItems.value = Object.entries(equippedItemsData).map(([instanceId, item]) => {
                  const tpl = gameItemsData[item.templateId] || {};
                  const states = item.states || {};
                  return {
                    id: instanceId,
                    instanceId,
                    name: tpl.name || item.templateId,
                    type: tpl.type,
                    ...tpl,
                    ...states,
                  };
                });
              } else {
                equippedItems.value = [];
              }
              // Fetch equipped vehicle
              const vehicleData = charPrivateData.vehicleData || null;
              if (vehicleData) {
                const template = gameItemsData[vehicleData.templateId] || {};
                equippedVehicle.value = {
                  id: vehicleId,
                  instanceId: vehicleId,
                  ...template,
                  ...vehicleData,
                  fuelLevel: vehicleData.states?.fuelLevel || 0,
                };
              } else {
                equippedVehicle.value = null;
              }
              // V2: mechanical items from mechanicalItems object map
              const mechanicalItemsData = charPrivateData?.mechanicalItems || {};
              if (Object.keys(mechanicalItemsData).length > 0) {
                equippedMechanicalOrgans.value = Object.entries(mechanicalItemsData).map(([instanceId, item]) => {
                  const tpl = gameItemsData[item.templateId] || {};
                  const states = item.states || {};
                  return {
                    id: instanceId,
                    instanceId,
                    name: tpl.name || item.templateId,
                    ...tpl,
                    ...states,
                  };
                });
              } else {
                equippedMechanicalOrgans.value = [];
              }
              // V2: ghost items from ghostItems object map
              const ghostItemsData = charPrivateData?.ghostItems || {};
              const ghostTypes = charPrivateData?.ghostTypes || [];
              if (Object.keys(ghostItemsData).length > 0) {
                equippedGhosts.value = Object.entries(ghostItemsData).map(([instanceId, item], index) => {
                  const tpl = gameItemsData[item.templateId] || {};
                  const states = item.states || {};
                  return {
                    id: instanceId,
                    instanceId,
                    name: tpl.name || item.templateId,
                    subtype: ghostTypes[index] || states.subtype || tpl.subtype || 'วิญญาณ',
                    ...tpl,
                    ...states,
                  };
                });
              } else {
                equippedGhosts.value = [];
              }
              equippedLoading.value = false;
              updateLoadingState();
            } else {
              equippedItems.value = [];
              equippedVehicle.value = null;
              equippedMechanicalOrgans.value = [];
              equippedGhosts.value = [];
              equippedItemTypes.value = [];
              equippedError.value = 'ไม่พบข้อมูลตัวละคร';
              equippedLoading.value = false;
              updateLoadingState();
            }
          },
          err => {
            equippedError.value = err.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูลไอเทม';
            equippedLoading.value = false;
            updateLoadingState();
          }
        );
      return unsubscribe;
    };
    // V2 helper: map items object to array with templates
    const mapItemsObject = (itemsObj, invalidList) => {
      return Object.entries(itemsObj || {}).map(([instanceId, itemData]) => {
        const tpl = gameItemsData[itemData.templateId] || {};
        if (!tpl.name && invalidList) {
          invalidList.push(instanceId);
        }
        return {
          id: instanceId,
          instanceId,
          name: tpl.name || itemData.templateId,
          quantity: itemData.states?.quantity || 1,
          ...tpl,
          ...itemData.states,
        };
      });
    };
    // Fetch Inventory Items from bags Collection — V2 schema
    const subscribeToInventoryItems = () => {
      if (!userId.value) {
        inventoryError.value = 'ไม่พบ ID ผู้ใช้';
        inventoryLoading.value = false;
        return () => {};
      }
      const unsubscribe = projectFirestore
        .collection('bags')
        .doc(userId.value)
        .onSnapshot(
          async doc => {
            invalidBagItems.value = [];
            if (doc.exists) {
              try {
                const bagData = doc.data();
                inventoryItems.value = mapItemsObject(bagData.items, invalidBagItems.value);
                currentWeight.value = bagData.currentWeight || 0;
                maxLoad.value = bagData.maxLoad || 100;
                inventoryLoading.value = false;
              } catch (err) {
                inventoryError.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลกระเป๋า: ' + err.message;
                inventoryItems.value = [];
                currentWeight.value = 0;
                maxLoad.value = 100;
                inventoryLoading.value = false;
              }
            } else {
              inventoryItems.value = [];
              currentWeight.value = 0;
              maxLoad.value = 100;
              inventoryError.value = 'ไม่พบข้อมูลกระเป๋า';
              inventoryLoading.value = false;
            }
            updateLoadingState();
          },
          err => {
            inventoryError.value = err.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูลกระเป๋า';
            inventoryLoading.value = false;
            updateLoadingState();
          }
        );
      return unsubscribe;
    };
    // Fetch Storage Items from storages Collection
    const subscribeToStorageItems = () => {
      if (!userId.value) {
        storageError.value = 'ไม่พบ ID ผู้ใช้';
        storageLoading.value = false;
        return () => {};
      }
      const unsubscribe = projectFirestore
        .collection('storages')
        .doc(userId.value)
        .onSnapshot(
          async doc => {
            invalidStorageItems.value = [];
            if (doc.exists) {
              try {
                const storageData = doc.data();
                storageItems.value = mapItemsObject(storageData.items, invalidStorageItems.value);
                storageWeight.value = storageData.currentWeight || 0;
                storageMaxLoad.value = storageData.maxLoad || 100;
                storageLoading.value = false;
              } catch (err) {
                storageError.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลคลัง: ' + err.message;
                storageItems.value = [];
                storageWeight.value = 0;
                storageMaxLoad.value = 100;
                storageLoading.value = false;
              }
            } else {
              storageItems.value = [];
              storageWeight.value = 0;
              storageMaxLoad.value = 100;
              storageError.value = 'ไม่พบข้อมูลคลัง';
              storageLoading.value = false;
            }
            updateLoadingState();
          },
          err => {
            storageError.value = err.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูลคลัง';
            storageLoading.value = false;
            updateLoadingState();
          }
        );
      return unsubscribe;
    };
    // Update level and reset stats in both collections
    const updateLevel = async () => {
      if (!userId.value || !characterData.value || !newLevel.value || newLevel.value <= 0) return;
      isUpdating.value = true;
      error.value = null;
      try {
        const newStatPoints = (newLevel.value - 1) * 5;
        const resetStats = { stamina: 0, str: 0, int: 0, agi: 0, luck: 0 };
        await projectFirestore
          .collection('characterPrivate')
          .doc(userId.value)
          .update({
            level: parseInt(newLevel.value),
            statPoints: newStatPoints,
            stats: resetStats,
          });
        await projectFirestore
          .collection('characters')
          .doc(userId.value)
          .update({
            level: parseInt(newLevel.value),
          });
        newLevel.value = null;
      } catch (err) {
        console.error('Error updating level:', err);
        error.value = `เกิดข้อผิดพลาดในการอัปเดตเลเวล: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };
    // Update silver in characters
    const updateSilver = async () => {
      if (!userId.value || !characterData.value || !addSilver.value || addSilver.value < 0) return;
      isUpdating.value = true;
      error.value = null;
      try {
        await projectFirestore
          .collection('characters')
          .doc(userId.value)
          .update({
            silver: firebase.firestore.FieldValue.increment(parseInt(addSilver.value)),
          });
        addSilver.value = null;
      } catch (err) {
        console.error('Error updating silver:', err);
        error.value = `เกิดข้อผิดพลาดในการอัปเดต silver: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };
    // Update gold in characters
    const updateGold = async () => {
      if (!userId.value || !characterData.value || !addGold.value || addGold < 0) return;
      isUpdating.value = true;
      error.value = null;
      try {
        await projectFirestore
          .collection('characters')
          .doc(userId.value)
          .update({
            gold: firebase.firestore.FieldValue.increment(parseInt(addGold.value)),
          });
        addGold.value = null;
      } catch (err) {
        console.error('Error updating gold:', err);
        error.value = `เกิดข้อผิดพลาดในการอัปเดต gold: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };
    // Update diamond in characters
    const updateDiamond = async () => {
      if (!userId.value || !characterData.value || !addDiamond.value || addDiamond < 0) return;
      isUpdating.value = true;
      error.value = null;
      try {
        await projectFirestore
          .collection('characters')
          .doc(userId.value)
          .update({
            diamond: firebase.firestore.FieldValue.increment(parseInt(addDiamond.value)),
          });
        addDiamond.value = null;
      } catch (err) {
        console.error('Error updating diamond:', err);
        error.value = `เกิดข้อผิดพลาดในการอัปเดต diamond: ${err.message}`;
      } finally {
        isUpdating.value = false;
      }
    };
    // Update itemStats
    const updateItemStats = async () => {
      isUpdating.value = true;
      try {
        await projectFirestore.collection('characterPrivate').doc(userId.value).update({
          itemStats: calculatedItemStats.value
        });
      } catch (err) {
        error.value = 'Error updating itemStats: ' + err.message;
      } finally {
        isUpdating.value = false;
      }
    };
    // Update ghostStats
    const updateGhostStats = async () => {
      isUpdating.value = true;
      try {
        await projectFirestore.collection('characterPrivate').doc(userId.value).update({
          ghostStats: calculatedGhostStats.value
        });
      } catch (err) {
        error.value = 'Error updating ghostStats: ' + err.message;
      } finally {
        isUpdating.value = false;
      }
    };
    // Update tattooStats
    const updateTattooStats = async () => {
      isUpdating.value = true;
      try {
        await projectFirestore.collection('characterPrivate').doc(userId.value).update({
          tattooStats: calculatedTattooStats.value
        });
      } catch (err) {
        error.value = 'Error updating tattooStats: ' + err.message;
      } finally {
        isUpdating.value = false;
      }
    };
    // Update skillStats
    const updateSkillStats = async () => {
      isUpdating.value = true;
      try {
        await projectFirestore.collection('characterPrivate').doc(userId.value).update({
          skillStats: calculatedSkillStats.value
        });
      } catch (err) {
        error.value = 'Error updating skillStats: ' + err.message;
      } finally {
        isUpdating.value = false;
      }
    };
    // Update titleStats
    const updateTitleStats = async () => {
      isUpdating.value = true;
      try {
        await projectFirestore.collection('characterPrivate').doc(userId.value).update({
          titleStats: calculatedTitleStats.value
        });
      } catch (err) {
        error.value = 'Error updating titleStats: ' + err.message;
      } finally {
        isUpdating.value = false;
      }
    };
    // Update bonusStats
    const updateBonusStats = async () => {
      isUpdating.value = true;
      try {
        await projectFirestore.collection('characterPrivate').doc(userId.value).update({
          bonusStats: calculatedBonusStats.value
        });
      } catch (err) {
        error.value = 'Error updating bonusStats: ' + err.message;
      } finally {
        isUpdating.value = false;
      }
    };
    // Update bag weight
    const updateBagWeight = async () => {
      isUpdating.value = true;
      try {
        await projectFirestore.collection('bags').doc(userId.value).update({
          currentWeight: calculatedBagWeight.value
        });
      } catch (err) {
        error.value = 'Error updating bag weight: ' + err.message;
      } finally {
        isUpdating.value = false;
      }
    };
    // Update storage weight
    const updateStorageWeight = async () => {
      isUpdating.value = true;
      try {
        await projectFirestore.collection('storages').doc(userId.value).update({
          currentWeight: calculatedStorageWeight.value
        });
      } catch (err) {
        error.value = 'Error updating storage weight: ' + err.message;
      } finally {
        isUpdating.value = false;
      }
    };
    // Remove invalid bag items — V2 schema
    const removeInvalidBagItems = async () => {
      isUpdating.value = true;
      try {
        const bagRef = projectFirestore.collection('bags').doc(userId.value);
        const bagDoc = await bagRef.get();
        if (bagDoc.exists) {
          const updates = {};
          invalidBagItems.value.forEach(id => {
            updates[`items.${id}`] = FieldValue.delete();
          });
          updates.currentWeight = calculatedBagWeight.value;
          await bagRef.update(updates);
          invalidBagItems.value = [];
        }
      } catch (err) {
        error.value = 'Error removing invalid bag items: ' + err.message;
      } finally {
        isUpdating.value = false;
      }
    };
    // Remove invalid storage items — V2 schema
    const removeInvalidStorageItems = async () => {
      isUpdating.value = true;
      try {
        const storageRef = projectFirestore.collection('storages').doc(userId.value);
        const storageDoc = await storageRef.get();
        if (storageDoc.exists) {
          const updates = {};
          invalidStorageItems.value.forEach(id => {
            updates[`items.${id}`] = FieldValue.delete();
          });
          updates.currentWeight = calculatedStorageWeight.value;
          await storageRef.update(updates);
          invalidStorageItems.value = [];
        }
      } catch (err) {
        error.value = 'Error removing invalid storage items: ' + err.message;
      } finally {
        isUpdating.value = false;
      }
    };
    // Lifecycle Hooks
    let unsubscribeUserData = () => {};
    let unsubscribeCharacterData = { unsubscribeCharacter: () => {}, unsubscribePrivate: () => {} };
    let unsubscribeEquippedItems = () => {};
    let unsubscribeInventoryItems = () => {};
    let unsubscribeStorageItems = () => {};
    onMounted(() => {
      loadSortOrder();
      unsubscribeUserData = subscribeToUserData();
      unsubscribeCharacterData = subscribeToCharacterData();
      unsubscribeEquippedItems = subscribeToEquippedItems();
      unsubscribeInventoryItems = subscribeToInventoryItems();
      unsubscribeStorageItems = subscribeToStorageItems();
      fetchLogs();
      const logsContainer = document.querySelector('.logs-list');
      if (logsContainer) {
        logsContainer.addEventListener('scroll', handleScroll);
      }
    });
    onUnmounted(() => {
      unsubscribeUserData();
      unsubscribeCharacterData.unsubscribeCharacter();
      unsubscribeCharacterData.unsubscribePrivate();
      unsubscribeEquippedItems();
      unsubscribeInventoryItems();
      unsubscribeStorageItems();
      const logsContainer = document.querySelector('.logs-list');
      if (logsContainer) {
        logsContainer.removeEventListener('scroll', handleScroll);
      }
    });
    return {
      activeTab,
      tabs,
      characterData,
      characterPrivateData,
      userData,
      loading,
      error,
      toggleModal,
      selectedStat,
      selectedPower,
      selectedItem,
      showDetailModal,
      isUpdating,
      newLevel,
      addSilver,
      addGold,
      addDiamond,
      currentTitle,
      itemNames,
      ghostItems,
      playerMaxHp,
      playerHp,
      hpPercentage,
      energyPercentage,
      foodPercentage,
      happinessPercentage,
      infectionPercentage,
      totalStats,
      calculatedStats,
      calculatedNormalItemStats,
      calculatedMechanicalItemStats,
      calculatedItemStats,
      calculatedGhostStats,
      calculatedTattooStats,
      calculatedSkillStats,
      calculatedTitleStats,
      calculatedBonusStats,
      isItemStatsMatching,
      isGhostStatsMatching,
      isTattooStatsMatching,
      isSkillStatsMatching,
      isTitleStatsMatching,
      isBonusStatsMatching,
      calculatedBagWeight,
      calculatedStorageWeight,
      isBagWeightMatching,
      isStorageWeightMatching,
      invalidBagItems,
      invalidStorageItems,
      nextLevelExp,
      experiencePercentage,
      equippedItems,
      equippedVehicle,
      equippedMechanicalOrgans,
      equippedGhosts,
      equippedItemTypes,
      equippedLoading,
      equippedError,
      allItemTypes,
      equippedByType,
      mechanicalNamesSkill1,
      mechanicalNamesSkill2,
      mechanicalNamesSkill3,
      mechanicalByName,
      ghostSubtypes,
      isGhostSubtypeEquipped,
      typeIcons,
      statOrder,
      statMeta,
      getStat,
      formatBonus,
      setProgress,
      missingItemTypes,
      inventoryItems,
      currentWeight,
      maxLoad,
      inventoryLoading,
      inventoryError,
      inventorySelectedType,
      inventorySearchQuery,
      storageItems,
      storageWeight,
      storageMaxLoad,
      storageLoading,
      storageError,
      storageSelectedType,
      storageSearchQuery,
      tattoos,
      skills,
      isSorting,
      showSortModal,
      sortContext,
      typeOptions,
      sortOrder,
      logs,
      filterAction,
      searchQuery,
      logsLoading,
      logsError,
      hasMoreLogs,
      handleImageError,
      selectItem,
      openDetailModal,
      goBack,
      showStatDetails,
      statBreakdown,
      showPowerDetails,
      powerBreakdown,
      updateLevel,
      updateSilver,
      updateGold,
      updateDiamond,
      updateItemStats,
      updateGhostStats,
      updateTattooStats,
      updateSkillStats,
      updateTitleStats,
      updateBonusStats,
      updateBagWeight,
      updateStorageWeight,
      removeInvalidBagItems,
      removeInvalidStorageItems,
      openSortModal,
      moveUp,
      moveDown,
      saveSortOrder,
      resetSortOrder,
      getActionName,
      formatTimestamp,
      checkWarnings,
      filteredLogs,
      applyFilters,
      loadMoreLogs,
    };
  },
};
</script>
<style scoped>
#style-1::-webkit-scrollbar {
  width: 8px;
  background-color: #2d3748;
}
#style-1::-webkit-scrollbar-thumb {
  background-color: #4a5568;
  border-radius: 4px;
}
#style-1::-webkit-scrollbar-thumb:hover {
  background-color: #718096;
}
.bg {
  background: linear-gradient(to bottom, #1a202c, #2d3748);
}
.rarity-text {
  font-size: 0.65rem;
  color: #e2e8f0;
}
.rarity-icon {
  font-size: 0.65rem;
}
.animate-spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
#style-1 {
  scrollbar-width: thin;
  scrollbar-color: #4b5563 #374151;
}
button:not(:disabled):active,
select:not(:disabled):active,
input:not(:disabled):active {
  transform: scale(0.98);
}
</style>
